/** Typed API client for the control-plane + analysis services (via Vite dev proxy). */

// In dev, calls go through the Vite proxy prefixes; in prod the SPA and API share an origin
// (behind the same ALB) so calls are same-origin and the ALB routes /v1/* to the services.
const SAME_ORIGIN = import.meta.env.PROD;
const CONTROL = SAME_ORIGIN ? '' : '/api/control';
const ANALYSIS = SAME_ORIGIN ? '' : '/api/analysis';
const INTERACTIVE = SAME_ORIGIN ? '' : '/api/interactive';
// Growth loop (referrals + attribution). Same-origin in prod; the ALB routes /v1/growth/* to growth-svc.
const GROWTH = SAME_ORIGIN ? '' : '/api/growth';

/**
 * COOKIE AUTH.
 *
 * When on, the session lives in httpOnly cookies set by the control plane and this module never
 * touches localStorage. That is the point: an XSS anywhere on the origin can no longer read a live
 * access token OR its refresh token.
 *
 * ON by default. It shipped off while the server side was being proven; the server accepts BOTH
 * schemes, so this is a client-only decision and `VITE_COOKIE_AUTH=false` reverts it with a rebuild
 * rather than a migration. Leaving it off by default would have meant the security fix silently did
 * not apply in production unless someone remembered an env var - which is the kind of thing that
 * gets forgotten and then reported as "the vulnerability is still there".
 */
const COOKIE_AUTH = import.meta.env.VITE_COOKIE_AUTH !== 'false';

/** Non-httpOnly cookies the page is meant to read. The token cookies are deliberately not here. */
const TENANT_COOKIE = 'ma_tid';
const CSRF_COOKIE = 'ma_csrf';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 1) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    try {
      return decodeURIComponent(part.slice(eq + 1).trim());
    } catch {
      return part.slice(eq + 1).trim();
    }
  }
  return null;
}

// In cookie mode these stay null forever - the browser holds the tokens and JS cannot see them.
let accessToken: string | null = COOKIE_AUTH ? null : localStorage.getItem('tl_access') ?? null;
let tenantId: string | null = COOKIE_AUTH ? readCookie(TENANT_COOKIE) : localStorage.getItem('tl_tenant') ?? null;
let refreshToken: string | null = COOKIE_AUTH ? null : localStorage.getItem('tl_refresh') ?? null;

/**
 * Roles and identity, from `GET /v1/auth/session`.
 *
 * In cookie mode the client cannot decode its own token, so the server is the only source of truth
 * for who the caller is - which is strictly better than the client deciding. `null` means "not asked
 * yet", which callers must treat as "unknown", NOT as "anonymous".
 */
let serverSession: { authenticated: boolean; userId: string | null; tenantId: string | null; roles: string[] } | null = null;

// Session change broadcast: the chrome (nav, agent widget, bell) must flip auth state the moment a
// sign-in/sign-out/refresh-failure changes it, without waiting for a route change or reload. The
// snapshot object is only replaced when the session actually changes, so it is safe to consume via
// React's useSyncExternalStore (a stable reference between changes, no render loops).
let sessionSnapshot: { accessToken: string | null; tenantId: string | null } = {
  // In cookie mode there is no readable token, so the snapshot carries a marker instead. Callers
  // must use isSignedIn() rather than treating this as a bearer token.
  accessToken: COOKIE_AUTH ? (readCookie(CSRF_COOKIE) ? 'cookie' : null) : accessToken,
  tenantId,
};
const sessionListeners = new Set<() => void>();

/** Subscribe to session (sign-in/sign-out/rotation) changes. Returns the unsubscribe function. */
export function subscribeSession(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

export function setSession(token: string | null, tenant: string | null, refresh?: string | null) {
  if (COOKIE_AUTH) {
    // The cookies are already set (or cleared) by the control plane's response; persisting anything
    // here would recreate exactly the exposure this migration removes. Only the in-memory view and
    // the subscriber broadcast are ours.
    tenantId = tenant ?? readCookie(TENANT_COOKIE);
    if (token === null) {
      serverSession = null;
      accessToken = null;
      refreshToken = null;
    } else if (token && !readCookie(CSRF_COOKIE)) {
      // The server authenticated us but set no session cookies, which means a control plane older
      // than this build - the deploy-skew window where the SPA has shipped and the API has not.
      // Hold the returned token IN MEMORY ONLY (never localStorage, so the exposure this migration
      // removes does not come back) and let the Bearer path carry it. Without this the user would
      // get a signed-in shell in which every single request 401s. Self-corrects once the API ships.
      accessToken = token;
      refreshToken = refresh ?? null;
    }
    sessionSnapshot = { accessToken: token === null ? null : 'cookie', tenantId };
    for (const listener of sessionListeners) listener();
    return;
  }
  accessToken = token;
  tenantId = tenant;
  if (token) localStorage.setItem('tl_access', token);
  else localStorage.removeItem('tl_access');
  if (tenant) localStorage.setItem('tl_tenant', tenant);
  else localStorage.removeItem('tl_tenant');
  // A null access token means "signed out". Always clear the refresh token too. Otherwise
  // persist the refresh token so the access token can be silently renewed (15-min access TTL).
  if (token === null) {
    refreshToken = null;
    localStorage.removeItem('tl_refresh');
  } else if (refresh !== undefined) {
    refreshToken = refresh;
    if (refresh) localStorage.setItem('tl_refresh', refresh);
    else localStorage.removeItem('tl_refresh');
  }
  sessionSnapshot = { accessToken, tenantId };
  for (const listener of sessionListeners) listener();
}

/** True when the stored access token is missing or within `skewSeconds` of expiry. */
function accessTokenExpired(skewSeconds = 30): boolean {
  if (!accessToken) return true;
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return true;
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { exp?: number };
    return !claims.exp || claims.exp - skewSeconds <= Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
}

// Single-flight refresh: concurrent 401s share one refresh round-trip.
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Renews the access token from the stored refresh token (rotating it). Returns false and clears
 * the session on failure so the UI reflects a genuine sign-out instead of looping on a dead token.
 */
async function refreshAccessToken(): Promise<boolean> {
  // In cookie mode the refresh token is an httpOnly cookie: unreadable here, and sent automatically.
  if (!COOKIE_AUTH && !refreshToken) return false;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const csrf = readCookie(CSRF_COOKIE);
      const res = await fetch(`${CONTROL}/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(csrf ? { 'x-csrf-token': csrf } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(COOKIE_AUTH ? {} : { refreshToken }),
      });
      if (!res.ok) {
        setSession(null, null);
        return false;
      }
      const json = (await res.json()) as { accessToken: string; refreshToken: string };
      // Rotation: persist the new access + refresh pair; keep the existing tenant.
      setSession(json.accessToken, tenantId, json.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Ensures a non-expired access token before opening a WebSocket (which can't retry mid-stream). */
export async function ensureFreshAccessToken(): Promise<void> {
  // Cookie mode cannot read `exp`, so it refreshes unconditionally before an unretryable socket
  // rather than guessing. One extra round-trip beats a stream that dies mid-connection.
  if (COOKIE_AUTH) {
    if (isSignedIn()) await refreshAccessToken();
    return;
  }
  if (accessToken && accessTokenExpired()) await refreshAccessToken();
}

export function getSession() {
  return sessionSnapshot;
}

/**
 * Sign out: clear the local session synchronously FIRST (so subscribed UI - the nav, agent widget,
 * bell - flips to the anonymous state immediately, no reload), then revoke the captured refresh +
 * access tokens server-side best-effort so they can't be reused. Never throws; a network hiccup
 * still leaves the client signed out.
 */
export async function signOut(): Promise<void> {
  const capturedRefresh = refreshToken;
  const capturedAccess = accessToken;
  const csrf = readCookie(CSRF_COOKIE);
  setSession(null, null);
  try {
    // Cookie mode has nothing to capture - the server reads the refresh cookie and clears all of
    // them on the way out - so the call is made unconditionally there.
    if (COOKIE_AUTH || capturedRefresh) {
      await fetch(`${CONTROL}/v1/auth/logout`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(capturedAccess ? { authorization: `Bearer ${capturedAccess}` } : {}),
          ...(csrf ? { 'x-csrf-token': csrf } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(COOKIE_AUTH ? {} : { refreshToken: capturedRefresh }),
      });
    }
  } catch {
    // best-effort revocation; the local session is already cleared
  }
}

/**
 * True when there is a session, whichever transport carries it.
 *
 * Prefer this over `!!getSession().accessToken`: in cookie mode there IS no readable access token,
 * so the snapshot carries a marker rather than a credential.
 */
export function isSignedIn(): boolean {
  return sessionSnapshot.accessToken !== null;
}

/**
 * One-time migration for a user who was signed in BEFORE cookie auth shipped.
 *
 * On deploy day their browser holds `tl_access` and `tl_refresh` in localStorage and no cookies.
 * Cookie mode ignores those keys, so without this they are silently signed out - and, far worse,
 * their live access AND refresh tokens sit in localStorage forever: never read again, never cleared,
 * still readable by any XSS. That would defeat the entire point of the migration for exactly the
 * population it was meant to protect.
 *
 * So: present the legacy refresh token in the request BODY (the only way the server can accept it -
 * it cannot read a cookie that does not exist yet), let it set the cookies, and delete the legacy
 * keys either way. A failed exchange signs the user out, which is acceptable. Leaving a live refresh
 * token readable by JavaScript is not.
 *
 * Safe to call on every boot: it no-ops once the keys are gone.
 */
export async function migrateLegacySession(): Promise<void> {
  if (!COOKIE_AUTH) return;
  let legacyRefresh: string | null = null;
  let hasLegacy = false;
  try {
    legacyRefresh = localStorage.getItem('tl_refresh');
    // Gate on ANY legacy key, not just the refresh token. A session that stored an access token
    // without a refresh token would otherwise keep that token in localStorage forever - unusable,
    // unreadable by this build, and still exactly the exposure being removed.
    hasLegacy = !!legacyRefresh || !!localStorage.getItem('tl_access') || !!localStorage.getItem('tl_tenant');
  } catch {
    return; // storage unavailable; nothing to migrate
  }
  if (!hasLegacy) return;

  const clearLegacy = () => {
    try {
      localStorage.removeItem('tl_access');
      localStorage.removeItem('tl_refresh');
      localStorage.removeItem('tl_tenant');
    } catch {
      /* storage unavailable */
    }
  };

  // Already on cookies: nothing to exchange, but the stale token must still be swept up.
  if (readCookie(CSRF_COOKIE)) {
    clearLegacy();
    return;
  }

  // Nothing exchangeable (an access token alone cannot be renewed): just sweep it up.
  if (!legacyRefresh) {
    clearLegacy();
    return;
  }

  try {
    const res = await fetch(`${CONTROL}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ refreshToken: legacyRefresh }),
      // The first paint waits on this call, so it must be bounded: a hung network would otherwise
      // hold a white screen for the browser's full default timeout.
      signal: AbortSignal.timeout(5000),
    });
    if (res.ok) {
      const json = (await res.json()) as { orgId?: string | null };
      // The response set the cookies; sync the in-memory view and tell subscribers.
      tenantId = json.orgId ?? readCookie(TENANT_COOKIE);
      sessionSnapshot = { accessToken: readCookie(CSRF_COOKIE) ? 'cookie' : null, tenantId };
      for (const listener of sessionListeners) listener();
    }
  } catch {
    // Network failure: fall through and still clear. A signed-out user can sign in again; a
    // forgotten refresh token in localStorage is the thing we came here to remove.
  }
  clearLegacy();
}

/**
 * Fetches who the server thinks we are. Required in cookie mode, where the client cannot decode its
 * own token; harmless otherwise. Populates the cache that `getSessionRoles()` reads.
 */
export async function loadServerSession(): Promise<void> {
  if (!isSignedIn()) {
    serverSession = null;
    return;
  }
  try {
    const res = await req<{ authenticated: boolean; userId?: string | null; tenantId?: string | null; roles?: string[] }>(
      CONTROL,
      '/v1/auth/session',
    );
    const prev = serverSession;
    serverSession = {
      authenticated: !!res.authenticated,
      userId: res.userId ?? null,
      tenantId: res.tenantId ?? null,
      roles: res.roles ?? [],
    };
    if (!res.authenticated) {
      setSession(null, null);
      return;
    }
    if (res.tenantId && res.tenantId !== tenantId) {
      tenantId = res.tenantId;
    }
    // Replace the snapshot IDENTITY whenever the server told us something new (the first answer,
    // a role change, a tenant change). useSyncExternalStore consumers only re-render when
    // getSession() returns a NEW object, and the role gates (Admin nav item, the /admin route)
    // read getSessionRoles() during render: without this bump, an admin's first render is stuck
    // at roles=[] and the Admin surfaces never appear until some unrelated re-render.
    // The change guard is also the loop terminator: App refetches the session when the snapshot
    // changes, and the second, identical answer must not produce another new snapshot.
    const changed =
      !prev ||
      prev.authenticated !== serverSession.authenticated ||
      prev.userId !== serverSession.userId ||
      prev.roles.join(' ') !== serverSession.roles.join(' ') ||
      sessionSnapshot.tenantId !== tenantId;
    if (changed) sessionSnapshot = { accessToken: sessionSnapshot.accessToken, tenantId };
    for (const listener of sessionListeners) listener();
  } catch {
    // Leave the cache alone: a transient failure must not read as "you have no roles", which would
    // hide admin surfaces from an admin.
  }
}

/**
 * Whether getSessionRoles() is answering from real data yet. In cookie mode roles arrive
 * asynchronously from /v1/auth/session, so on a cold load the first render sees [] even for a
 * platform admin. Gates that merely HIDE a link may treat unknown as "no role", but a gate that
 * REDIRECTS must wait for this to go true, or it bounces real admins off /admin before the
 * answer lands. Signed-out is a final answer, and bearer mode decodes roles synchronously from
 * the token, so both count as known.
 */
export function sessionRolesKnown(): boolean {
  if (!isSignedIn()) return true;
  if (!COOKIE_AUTH) return true;
  return serverSession !== null;
}

/**
 * The signed-in user's id, or null.
 *
 * Cookie mode has no readable token, so this comes from the server session rather than from JWT
 * claims. Callers that key per-user browser state on it MUST handle null: decoding the unreadable
 * token instead silently yields one shared key for every account on the machine.
 */
export function getSessionUserId(): string | null {
  if (COOKIE_AUTH) return serverSession?.userId ?? null;
  if (!accessToken) return null;
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { sub?: string; uid?: string; userId?: string };
    return claims.sub ?? claims.uid ?? claims.userId ?? null;
  } catch {
    return null;
  }
}

export function getSessionRoles(): string[] {
  // Cookie mode: the token is unreadable, so roles come from the server. `null` means not yet asked,
  // and returning [] for that is correct - callers gate on a role being PRESENT.
  if (COOKIE_AUTH) return serverSession?.roles ?? [];
  if (!accessToken) return [];
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return [];
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    const claims = JSON.parse(atob(padded)) as { roles?: string[] };
    return claims.roles ?? [];
  } catch {
    return [];
  }
}

/**
 * Echoes the CSRF cookie back in a header on state-changing requests.
 *
 * Only same-origin JavaScript can read the cookie, so an attacker's page cannot reproduce this even
 * though the browser would happily attach the session cookie to their forged request. Safe methods
 * and Bearer-authenticated requests are exempt server-side, so sending it always is harmless.
 */
function applyCsrf(headers: Headers, method: string | undefined): void {
  const m = (method ?? 'GET').toUpperCase();
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return;
  const token = readCookie(CSRF_COOKIE);
  if (token) headers.set('x-csrf-token', token);
}

async function req<T>(base: string, path: string, init: RequestInit = {}, _retried = false): Promise<T> {
  // Proactively renew a soon-to-expire access token so authed calls don't 401 on the 15-min TTL.
  if (!COOKIE_AUTH && accessToken && !_retried && accessTokenExpired() && refreshToken) {
    await refreshAccessToken();
  }
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  if (tenantId) headers.set('x-tenant-id', tenantId);
  applyCsrf(headers, init.method);
  // `include`, not `same-origin`: the dev proxy and the prod ALB are same-origin, but being explicit
  // means a future cross-origin deployment does not silently stop sending the session.
  const res = await fetch(`${base}${path}`, { ...init, headers, credentials: 'include' });
  // On a 401 from an expired/invalid token, refresh once and retry transparently.
  const canRetry = COOKIE_AUTH ? isSignedIn() : !!accessToken && !!refreshToken;
  if (res.status === 401 && !_retried && canRetry) {
    if (await refreshAccessToken()) return req<T>(base, path, init, true);
  }
  const text = await res.text();
  // The body may be non-JSON: a CloudFront/ALB 502/503/504 HTML page, or an edge timeout. Parse
  // defensively so callers see a clean ApiError ("Service unavailable, please retry") instead of a
  // raw "SyntaxError: Unexpected token '<'" leaking into the UI.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- defensive parse of untyped JSON/HTML error bodies
  let json: any = {};
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      if (!res.ok) {
        const msg =
          res.status >= 500
            ? 'Service temporarily unavailable, please retry.'
            : res.status === 429
              ? 'Rate limit reached, please slow down and retry.'
              : res.statusText || `Request failed (${res.status})`;
        throw new ApiError(res.status, msg);
      }
      throw new ApiError(res.status || 502, 'Unexpected non-JSON response from the server.');
    }
  }
  if (!res.ok) throw new ApiError(res.status, json?.error?.message ?? res.statusText, json?.error);
  return json as T;
}

/**
 * Authenticated fetch that returns the RAW `Response`, for endpoints whose body is not JSON.
 *
 * The report download used a bare `fetch()` and therefore sent no credentials at all, so a PRIVATE
 * scan or report answered 404 (`canViewScan` cannot see an anonymous caller) and the button reported
 * "Report unavailable (404)" on a report the owner was looking at. `req()` cannot be reused because it
 * parses the body as JSON, and these responses are a PDF or a Word document.
 *
 * Carries the same token handling as `req`: refresh a soon-to-expire token up front, and on a 401
 * refresh once and retry.
 */
export async function authorizedFetch(url: string, init: RequestInit = {}, _retried = false): Promise<Response> {
  if (!COOKIE_AUTH && accessToken && !_retried && accessTokenExpired() && refreshToken) {
    await refreshAccessToken();
  }
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  if (tenantId) headers.set('x-tenant-id', tenantId);
  applyCsrf(headers, init.method);
  const res = await fetch(url, { ...init, headers, credentials: 'include' });
  // In cookie mode there is no readable token to gate the retry on, so the gate is "do we believe we
  // have a session" instead.
  const canRetry = COOKIE_AUTH ? isSignedIn() : !!accessToken && !!refreshToken;
  if (res.status === 401 && !_retried && canRetry) {
    if (await refreshAccessToken()) return authorizedFetch(url, init, true);
  }
  return res;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
  }
}

export interface UrlScanVerdictSignal {
  id: string;
  label: string;
  category: string;
  weight: number;
  detail?: string;
}

export interface UrlScanResultView {
  schemaVersion: number;
  uuid: string;
  status: 'queued' | 'running' | 'partial' | 'done' | 'error';
  partial: boolean;
  task: {
    uuid: string;
    url: string;
    submittedUrl: string;
    visibility: string;
    tier: string;
    time: string;
    tags: string[];
    options: { vantages: string[]; useResidential: boolean; tor: boolean; grabFiles: boolean; interaction: string };
  };
  /** Malware samples correlated to this URL/domain ("which malware talks to this URL"). */
  correlations?: {
    total: number;
    hosting: boolean;
    samples: {
      sha256: string;
      family: string | null;
      name: string | null;
      edgeKind: 'c2' | 'download' | 'config' | 'contacted' | 'referenced' | 'dns';
      hosting: boolean;
      firstSeen: string;
      lastSeen: string;
    }[];
  };
  page: {
    url: string;
    domain: string;
    ip?: string;
    asn?: string;
    asnname?: string;
    country?: string;
    ptr?: string;
    server?: string;
    status?: number;
    title?: string;
    mimeType?: string;
    tlsIssuer?: string;
    tlsValidTo?: string;
    redirected: boolean;
    apexDomain?: string;
    umbrellaRank?: number;
    urlHistory?: { url: string; status: number }[];
    responseHeaders?: Record<string, string>;
  };
  registration?: {
    domain: string;
    registrar?: string;
    createdDate?: string;
    updatedDate?: string;
    expiresDate?: string;
    ageDays?: number;
    nameservers?: string[];
    registrantOrganization?: string;
    registrantCountry?: string;
    statuses?: string[];
    source: 'rdap' | 'whois';
  };
  /**
   * Who operates the site, from evidence rather than inference. `operator` is null whenever nothing
   * establishes one - which is the normal case for a free domain-validated certificate - and must be
   * rendered as "not asserted" rather than filled in from the hostname.
   */
  /**
   * Per-brand relationship. `brands` (on verdicts) remains the flat list of brands involved; this says
   * whether the page BELONGS to each one or IMPERSONATES it, with the provenance of the conclusion.
   */
  brandAssessments?: {
    brand: string;
    label: string;
    relationship: 'official' | 'impersonating' | 'mentions' | 'unrelated';
    confidence: number;
    sources: string[];
    evidence: string[];
  }[];
  impersonates?: string[];
  attribution?: {
    certOrganization?: string;
    certIssuerOrganization?: string;
    certAssurance: 'organization-validated' | 'domain-validated' | 'self-signed' | 'none';
    registrantOrganization?: string;
    registrar?: string;
    registrantCountry?: string;
    domainAgeDays?: number;
    networkOperator?: string;
    operator: string | null;
    operatorSource: 'certificate' | 'registrant' | null;
    evidence: string[];
  };
  lists: {
    domains: string[];
    ips: string[];
    urls: string[];
    asns: string[];
    servers: string[];
    countries: string[];
    certificates: { subject: string; issuer: string; validFrom: string; validTo: string; fingerprintSha256: string; subjectAltNames: string[]; serialNumber?: string }[];
    hashes: string[];
    linkDomains: string[];
  };
  data: {
    requests: { url: string; method: string; status: number; type?: string; mimeType?: string; ip?: string; size?: number; server?: string; hash?: string }[];
    cookies: { name: string; domain: string; path?: string; httpOnly?: boolean; secure?: boolean; sameSite?: string; value?: string; expires?: number }[];
    console: { level: string; text: string; source?: string; url?: string; line?: number }[];
    links: { href: string; text: string }[];
    globals: string[];
    websockets: string[];
    frames?: { url: string; name?: string }[];
    technologies?: { name: string; category?: string }[];
    ipDetails?: { ip: string; countryCode?: string; countryName?: string; asn?: string; asOrg?: string; isp?: string; abuseConfidenceScore?: number; abuseTotalReports?: number; abuseUsageType?: string; isTor?: boolean }[];
    downloads: { url: string; filename?: string; sha256?: string; sandboxTaskId?: string; quarantined?: boolean }[];
    qrCodes: { data: string; isUrl: boolean }[];
  };
  meta: {
    processors: string[];
    scrubbed: boolean;
    scrubberFindings: { location: string; kind: string; param?: string; preview: string }[];
    safety: { egressLocked: boolean; pinnedAddress?: string; redirectHops: number; vantage: string };
    timings: { fastPassMs?: number; deepPassMs?: number; firstSignalMs?: number; totalMs?: number };
    cacheHit: boolean;
    capabilities?: {
      staticFastPass: boolean;
      deepPass: boolean;
      deepPassAvailable: boolean;
      /** A deep-pass job was queued to the isolated browser fleet; the result will be enriched when it lands. */
      deepPassQueued?: boolean;
      residentialEgress: boolean;
      torEgress: boolean;
      multiVantage: boolean;
    };
  };
  stats: { uniqIps: number; uniqDomains: number; uniqCountries: number; requests: number; securePercentage: number; malicious: number };
  /** Antivirus + YARA lines from scanning the page's own bytes and its scripts. */
  engines?: { engine: string; source: string; category: string; flagged: boolean; malwareName?: string; target?: 'download' }[];
  /** `targets` selected vs `scanned` processed, so partial coverage is visible. */
  engineStats?: { total: number; flagged: number; targets: number; scanned: number };
  /** Tier configured but produced nothing: zero detections means UNKNOWN, never clean. */
  enginesDegraded?: boolean;
  verdicts: {
    score: number;
    verdict: 'malicious' | 'suspicious' | 'benign' | 'unknown';
    confidence: number;
    malicious: boolean;
    brands: string[];
    categories: string[];
    subscores: { ourEngine: number; thirdParty: number; community: number };
    signals: UrlScanVerdictSignal[];
  };
  intel: {
    corpusHits: { indicator: string; kind: string; source: string; note?: string }[];
    kitFamily?: { name: string; confidence: number; artifacts?: string[] };
    infraCluster?: { id: string; confidence: number; note?: string };
    actor?: { name: string; confidence: number };
    pdns?: { indicator: string; records: { value: string; firstSeen?: string; lastSeen?: string }[] }[];
    ctPivots?: { indicator: string; certs: string[]; siblingDomains?: string[] }[];
    homograph?: { isIdn: boolean; unicode: string; skeleton: string; findings: string[]; brandMatch?: { brand: string; kind: string; distance: number } };
  };
  evidence: { faviconMmh3?: number; screenshotUrl?: string; domUrl?: string; domSha256?: string; screenshotSha256?: string; harUrl?: string };
}

export interface UrlScanDocView {
  uuid: string;
  pageUrl: string;
  pageDomain: string;
  pageIp?: string;
  verdict: string;
  score: number;
  brands: string[];
  time: string;
  /** Malware samples correlated to this URL (backs the sample:/family: advanced search). */
  sampleHashes?: string[];
  families?: string[];
  /** Brands this scan EVIDENCED as impersonated - distinct from `brands` (merely involved). */
  impersonates?: string[];
  kit?: string;
}

export interface Verdict {
  verdict: 'malicious' | 'suspicious' | 'clean' | 'unknown' | 'insufficient';
  score: number;
  family?: string;
  /** Neutral labels (e.g. a family candidate demoted on a non-malicious verdict). Shown as plain tags,
   *  never as the asserted family. */
  tags?: string[];
  /** Composed threat classification voted from scanner detection names (category + platform), a richer
   *  label beside `family` (e.g. "Windows Stealer"). Published only on a malicious/suspicious verdict. */
  threatCategory?: string;
  threatPlatform?: string;
  threatLabel?: string;
  /** Full per-category scanner vote table (token + distinct-scanner count). */
  categories?: { token: string; count: number }[];
  attackTechniques: string[];
  signals: {
    id: string;
    source: string;
    description: string;
    weight: number;
    confidence?: number;
    rule?: string;
    attack?: string[];
    engine?: string;
    machineGenerated?: boolean;
    evidence?: string[];
  }[];
  /** Reproducibility stamps (Track C). */
  fusionVersion?: string;
  weightsVersion?: string;
  rulePackHash?: string;
  coverage?: number;
}

export interface UsageInfo {
  dailyLookups: { used: number; base: number; rewardEarned: number; effective: number; remaining: number; resetAt: number };
  rewards: { uniqueSamplesToday: number; creditsPerUniqueSample: number; cap: number };
  submissionsToday: number;
  submissionsTotal: number;
}

export interface MySubmission {
  taskId: string;
  contentHash: string;
  state: string;
  verdict: string | null;
  score: number | null;
  reportReady: boolean;
  reportUrl: string | null;
  createdAt: string;
  rewarded: boolean;
}

export interface OnlineCounts {
  registered: number;
  anonymous: number;
  total: number;
}

/**
 * Real concurrency ceiling for one detonation class, with the inputs it is derived from so the
 * surface can show its own arithmetic instead of an unsourced number.
 */
export interface DetonationClassLimit {
  class: string;
  label: string;
  perWorker: number;
  workers: number;
  max: number;
  /** False when replicas report different caps (a deploy rolling through). */
  uniform?: boolean;
  source: string;
}

/** MEASURED guest occupancy for one detonation tier. Nulls mean unmeasurable, never zero. */
export interface GuestTierOccupancy {
  tier: string;
  label: string;
  busy: number | null;
  capacity: number | null;
  source: string;
  /** Present only when a field is null: why the host could not report it. */
  reason?: string;
}

export interface PlatformStatus {
  submissions: { totalSinceDeploy: number; today: number; fulfilled: number; fulfilledToday: number; failed: number; lastHourThroughput: number };
  queue: { depth: number; running: number; longestEtaSeconds: number };
  /**
   * Raw task-state census. OPERATOR-ONLY: `/v1/status` omits it entirely for anyone without the
   * platform-admin role, so it is optional here and the UI must key its panel off presence rather
   * than assume it arrived.
   */
  states?: Record<string, number>;
  fleet: {
    /**
     * DEPRECATED PAIR. `running` is analyses in the dynamic stage and `max` is ONE worker process's
     * guest cap, so they are NOT a ratio - rendering them as one produced an impossible "26 of 20
     * guests in use". Read `guests` and `inFlight` instead. Retained because the source-ingest
     * intake throttle consumes them.
     */
    running: number;
    max: number;
    /**
     * Per-class admission ceilings that bind BEFORE the global guest cap. Absent/empty when no live
     * worker has reported, in which case nothing is rendered - empty means "not published", never
     * "unlimited".
     */
    classLimits?: DetonationClassLimit[];
    /** Analyses in the dynamic stage vs the summed platform ceiling (`max` null = not published). */
    inFlight?: { count: number; max: number | null; source: string };
    /** Guests physically busy, measured from the detonation hosts. */
    guests?: {
      busy: number | null;
      capacity: number | null;
      /** False when the reading covers only part of the fleet, or a tier cannot state a total. */
      complete: boolean;
      tiers: GuestTierOccupancy[];
      measuredAt: string | null;
    };
  };
  /**
   * Distinct public reports in the corpus. `null` means not measured (the search index is the only
   * cheap source of a distinct count) and must render as such, never as zero. This is the ONLY field
   * that may be described as samples analysed: `submissions.totalSinceDeploy` counts task rows,
   * including failures and re-analyses of the same file.
   */
  corpus?: { publicSamples: number | null; source: string };
  /**
   * Detection engines a recent analysis actually ran, as a median over public reports from the last
   * seven days. Deliberately measured rather than read off the configured catalog, so a dormant
   * engine is never counted as coverage. `null` when unmeasurable.
   */
  engines?: { measured: number | null; sampleSize: number; source: string };
  /** Live users online (registered + anonymous), from the shared presence tracker. */
  online?: OnlineCounts;
  /** Feature flags the SPA reads to gate optional modules (e.g. the Sample Vault). */
  features?: {
    sampleVault?: boolean;
    sampleVaultSharing?: boolean;
    sampleVaultBinaryDownload?: boolean;
    sampleVaultPublicReports?: boolean;
    sampleVaultCommunityBinaries?: boolean;
    sampleVaultVerifiedResearchersOnly?: boolean;
    /** True when the Cyble Vision engine is live (key configured and not disabled). */
    cybleVision?: boolean;
    /** True while Cyble Vision is deliberately disabled for an upgrade ("Upgrade in progress"). */
    cybleVisionUpgrading?: boolean;
  };
  generatedAt: string;
}

// ---- Sample Vault (Phase 1: private single-owner) ----
export type VaultVisibilityState = 'private' | 'shared' | 'unlisted' | 'public';
export interface VaultVisibility {
  metadata: VaultVisibilityState;
  report: VaultVisibilityState;
  binaryDownload: 'private' | 'shared' | 'unlisted';
  contributorIdentity: VaultVisibilityState;
  collectionMembership: VaultVisibilityState;
}
export interface VaultRunSummary {
  runId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'canceled';
  taskId: string | null;
  reportRef: string | null;
  verdict: string | null;
  family: string | null;
  createdAt: string;
  completedAt: string | null;
}
/** Capability summary the backend projects per row (never wider than the authorizer decision). */
export interface VaultAccessSummary {
  isOwner: boolean;
  canViewMetadata: boolean;
  canViewReport: boolean;
  canDownloadBinary: boolean;
  canComment: boolean;
  canAddToCollection: boolean;
  canManage: boolean;
  via: string;
}
export interface VaultSampleView {
  id: string;
  sha256: string;
  originalFilename: string | null;
  notes?: string;
  source?: string | null;
  tags: string[];
  collectionIds: string[];
  uploadBatchId?: string | null;
  visibility: VaultVisibility;
  createdAt: string;
  updatedAt: string;
  latestRun: VaultRunSummary | null;
  /** Phase 2: present on list rows. `owned=false` rows are "shared with me". */
  owned?: boolean;
  access?: VaultAccessSummary;
  /** Present on shared rows when contributor identity is disclosed. */
  contributor?: { tenantId: string; userId: string } | null;
}
export type VaultPermissionSet = {
  viewMetadata: boolean;
  viewReport: boolean;
  downloadBinary: boolean;
  comment: boolean;
  addToCollection: boolean;
  reshare: boolean;
};
export interface VaultGrantView {
  id: string;
  vaultSampleId: string;
  granteeKind: 'user' | 'workspace';
  granteeId: string;
  permissions: VaultPermissionSet;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  active: boolean;
}
export interface VaultLinkView {
  id: string;
  vaultSampleId: string;
  tokenHint: string;
  permissions: VaultPermissionSet;
  createdAt: string;
  expiresAt: string | null;
  maxAccess: number | null;
  accessCount: number;
  revokedAt: string | null;
  active: boolean;
  /** Present ONLY in the create response (returned once). */
  token?: string;
}
export interface VaultCollectionView {
  id: string;
  name: string;
  description: string;
  count: number;
}
export interface StepUpResult {
  assertion: string;
  expiresIn: number;
  authTime: number;
  amr: string[];
}
export type VaultBulkAction =
  | 'tag'
  | 'untag'
  | 'add_to_collection'
  | 'remove_from_collection'
  | 'analyze'
  | 'delete'
  | 'share';
export interface VaultUsage {
  bytes: number;
  sampleCount: number;
  limitBytes: number;
  remainingBytes: number;
}
export interface VaultRunDetail {
  runId: string;
  status: string;
  taskId: string | null;
  reportRef: string | null;
  settings: { os?: string; engine?: string; network?: string };
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}
export interface VaultMultipartInit {
  key: string;
  uploadId: string;
}
// ---- Sample Vault (Phase 3: public-report publication pipeline) ----
export type SamplePublicationStatus = 'draft' | 'preview' | 'published' | 'unpublished' | 'under_moderation';
export interface PublicationContributorDisplay {
  anonymous: boolean;
  handle: string | null;
}
/** Owner view of a publication (the sanitized projection rides alongside when requested). */
export interface VaultPublicationView {
  id: string;
  vaultSampleId: string;
  sha256: string;
  status: SamplePublicationStatus;
  moderationState: 'none' | 'pending_review' | 'approved' | 'rejected';
  contributor: PublicationContributorDisplay;
  publishedAt: string | null;
  unpublishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  publicUrl: string | null;
  /** Present on publish/preview/get responses: the sanitized public projection (never the owner report). */
  sanitizedReport?: PublicReport;
}
/** A row in the public published-vault-report search/list surface. */
export interface VaultPublicationSummary {
  sha256: string;
  fileName: string | null;
  fileType: string | null;
  verdict: string | null;
  score: number | null;
  family: string | null;
  publishedAt: string | null;
  contributor: PublicationContributorDisplay;
}

// ---- Sample Vault (Phase 4: community malware exchange) ----
export type CommunityContributionStatus =
  | 'pending_review'
  | 'changes_requested'
  | 'approved'
  | 'rejected'
  | 'withdrawn'
  | 'taken_down';
export interface CommunityModerationNote {
  at: string;
  actorId: string;
  decision: string;
  note: string;
}
/** Owner view of their own contribution + moderation trail. */
export interface CommunityContributionView {
  id: string;
  vaultSampleId: string;
  sha256: string;
  fileType: string;
  size: number;
  status: CommunityContributionStatus;
  title: string;
  description: string;
  downloadable: boolean;
  active: boolean;
  reviewedAt: string | null;
  takedownReason: string | null;
  moderationTrail: CommunityModerationNote[];
  createdAt: string;
  updatedAt: string;
}
/** A row in the eligible-audience community browse surface (never owner identity or the report). */
export interface CommunityListingView {
  contributionId: string;
  vaultSampleId: string;
  sha256: string;
  fileType: string;
  size: number;
  title: string;
  description: string;
  contributedAt: string;
  approvedAt: string | null;
}
/** The caller's durable download-eligibility snapshot for a contributed sample. */
export interface CommunityEligibility {
  contributionApproved: boolean;
  authenticated: boolean;
  suspended: boolean;
  emailVerified: boolean;
  verifiedResearchersOnly: boolean;
  verifiedResearcher: boolean;
  termsAccepted: boolean;
  termsVersion: string;
  velocity24h: number;
  dailyDownloadCap: number;
  contributionId: string | null;
}
/** Admin moderation-queue item (owner identity visible to admins). */
export interface CommunityModerationItem {
  id: string;
  vaultSampleId: string;
  sha256: string;
  fileType: string;
  size: number;
  status: CommunityContributionStatus;
  title: string;
  description: string;
  reason: string;
  legalHold: boolean;
  tenantId: string;
  ownerUserId: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  takedownReason: string | null;
  moderationTrail: CommunityModerationNote[];
  createdAt: string;
  updatedAt: string;
}
export type ResearcherVerificationStatus = 'none' | 'pending' | 'approved' | 'rejected' | 'revoked';
export interface ResearcherVerificationView {
  userId: string;
  tenantId: string | null;
  status: ResearcherVerificationStatus;
  statement: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface MyResearcherStatus {
  status: ResearcherVerificationStatus;
  verified: boolean;
  decisionNote: string | null;
  verifiedResearchersOnly: boolean;
}
export interface CommunityAccessStateView {
  userId: string;
  tenantId: string | null;
  suspended: boolean;
  suspendedReason: string | null;
  suspendedBy: string | null;
  suspendedAt: string | null;
  abuseFlagged: boolean;
  abuseReason: string | null;
  updatedAt: string;
}
export interface TermsSection {
  heading: string;
  body: string;
}
export interface TermsDoc {
  id: 'tos' | 'aup';
  title: string;
  version: string;
  sections: TermsSection[];
}
export interface MyTermsStatus {
  currentVersion: string;
  accepted: boolean;
  acceptedVersion: string | null;
  acceptedAt: string | null;
}

/** Selectable time window for the Status page Activity panel. */
export type StatusWindow = '24h' | '7d' | '30d' | '180d' | '1y';
/** X-axis label granularity the chart uses to format bucket ticks. */
export type StatusGranularity = 'hour' | 'day' | 'week';

/** One bucket of platform activity (Status page charts); the bucket size varies by window. */
export interface StatusHistoryHour {
  t: string;
  submitted: number;
  completed: number;
  malicious: number;
  suspicious: number;
  clean: number;
  other: number;
}
/** One time bucket of PUBLIC URL-scan activity (separate population from the file-sample `hours`). */
export interface UrlActivityHour {
  t: string;
  scanned: number;
  malicious: number;
  suspicious: number;
  clean: number;
  other: number;
}
export interface StatusHistory {
  hours: StatusHistoryHour[];
  verdicts24h: { malicious: number; suspicious: number; clean: number; other: number; total: number };
  sources24h: Array<{ name: string; count: number }>;
  families24h: Array<{ name: string; count: number }>;
  generatedAt: string;
  /** The window these figures cover (echoed by the server; older servers omit it). */
  window?: StatusWindow;
  /** Bucket-tick granularity for the chart. */
  granularity?: StatusGranularity;
  /** Earliest day data exists (rollup windows only) so the UI can note limited history honestly. */
  coverageStart?: string | null;
  /** Additive: PUBLIC URL-scan activity over the same window (older servers omit these). */
  urlHours?: UrlActivityHour[];
  urlVerdicts24h?: { scanned: number; malicious: number; suspicious: number; clean: number; other: number };
}

/** One ATT&CK technique's corpus prevalence + trend + top families (cross-corpus navigator). */
export interface AttackTechniqueStat {
  key: string;
  count: number;
  recent: number;
  prev: number;
  families: { name: string; count: number }[];
}
export interface AttackNavigatorData {
  techniques: AttackTechniqueStat[];
  totalSamples: number;
  engine: string;
}

export type TokenPackId = '50' | '100' | '200';
export interface TokenPack {
  id: TokenPackId;
  priceUsd: number;
  /** Credits granted to the wallet on purchase. */
  credits: number;
  label: string;
}
export type PlanIdView = 'free' | 'hunter' | 'enterprise';
export interface PlanView {
  id: PlanIdView;
  name: string;
  tagline: string;
  monthlyPriceUsd: number;
  annualPriceUsd: number;
  /** Monthly credit allotment for the plan (unified wallet). */
  monthlyCredits: number;
  /** Legacy alias (== monthlyCredits for paid plans); kept for older clients. */
  monthlyAgentTokens?: number;
  /** Plan lookup ceiling; `null` = no override, follow the platform default. */
  dailyLookupQuota: number | null;
  /** Daily caps on FREE (uncharged) actions; -1 = unlimited. */
  dailyCaps?: { detonations: number };
  highlights: string[];
  features: {
    privateSubmissions: boolean;
    interactive: boolean;
    retrohunt: boolean;
    api: boolean;
    sso: boolean;
    priorityQueue: boolean;
  };
}
export interface Wallet {
  freeGranted: number;
  purchased: number;
  used: number;
  remaining: number;
  granted: number;
  autoRecharge: boolean;
  autoRechargePack: string | null;
}
export interface ReferralInfo {
  code: string;
  conversions: number;
  shareUrl: string;
  /** Server-driven reward amounts so the UI copy cannot drift from GROWTH_*_CREDITS. */
  refereeCredits?: number;
  referrerCredits?: number;
}
export interface GrowthFunnel {
  signups: number;
  invitesSent: number;
  referralsConverted: number;
  shares: number;
  kFactor: number;
}
export interface GrowthCohort {
  week: string;
  signups: number;
  retained: number;
}
export interface AgentCitation {
  kind: 'internal' | 'external';
  label: string;
  ref?: string;
}
export interface AgentResponse {
  answer: string;
  mode: 'quick' | 'advanced';
  citations: AgentCitation[];
  model: string;
  /** Credits charged for this answer (+ the raw LLM tokens consumed, for transparency). */
  usage: { credits: number; llmTokens?: number };
  wallet: { remaining: number | null };
}
export interface AgentSchedule {
  id: string;
  name: string;
  query: string;
  mode: 'quick' | 'advanced';
  intervalMinutes: number;
  enabled: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  createdAt: string;
}
// ---- Tier 1 retention engine ----
export type WatchKind =
  | 'query'
  | 'rule'
  | 'family'
  | 'actor'
  | 'domain'
  | 'ip'
  | 'hash'
  | 'url'
  | 'collection'
  | 'researcher'
  // Brand monitoring: alert when a NEW domain is evidenced impersonating the brand (scan sweep).
  | 'brand';
export interface Watch {
  id: string;
  tenantId: string;
  userId: string;
  kind: WatchKind;
  value: string;
  label: string;
  createdAt: string;
}
export type NotificationType = 'watch.hit' | 'livehunt.match' | 'verdict.changed' | 'digest' | 'system';
export interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  link: string | null;
  read: boolean;
  createdAt: string;
}
export interface NotificationPrefs {
  userId: string;
  webhookUrl: string | null;
  slackWebhookUrl: string | null;
  emailEnabled: boolean;
  digest: 'off' | 'daily' | 'weekly';
  muted: string[];
}
export interface HuntMatch {
  hash: string;
  fileName: string | null;
  rule: string;
  matchedStrings: string[];
  firstSeen: string;
}
export interface CampaignGraph {
  campaign: string;
  sampleCount: number;
  nodes: { id: string; type: string; label: string; props?: Record<string, unknown> }[];
  edges: { from: string; to: string; type: string }[];
}
export type RuleFormat = 'yara' | 'sigma';
export interface PublicRule {
  id: string;
  tenantId: string;
  authorId: string;
  authorHandle: string | null;
  name: string;
  description: string;
  format: RuleFormat;
  rule: unknown;
  tags: string[];
  forks: number;
  createdAt: string;
  updatedAt: string;
}
export interface StandingHunt {
  id: string;
  tenantId: string;
  userId: string;
  name: string;
  rule: unknown;
  createdAt: string;
}
export interface AgentRun {
  id: string;
  scheduleId: string | null;
  query: string;
  mode: 'quick' | 'advanced';
  answer: string;
  tokens: number;
  createdAt: string;
}

export interface ScalingDimension {
  desired: number;
  min: number;
  max: number;
}
export interface ScalingConfig {
  autoScaleEnabled: boolean;
  dailyBudgetUsd: number;
  monthlyBudgetUsd: number;
  detonationGuests: ScalingDimension;
  detonationHosts: ScalingDimension;
  analysisWorkers: ScalingDimension;
  apiTasks: ScalingDimension;
  openSearchNodes: ScalingDimension;
  redisNodes: ScalingDimension;
  updatedAt: string;
  updatedBy: string | null;
}
export interface CostBreakdown {
  monthlyUsd: number;
  dailyUsd: number;
  hourlyUsd: number;
  lines: { label: string; monthlyUsd: number }[];
}
export interface CostPreview {
  current: CostBreakdown;
  proposed: CostBreakdown;
  deltaMonthlyUsd: number;
  deltaDailyUsd: number;
  withinDailyBudget: boolean;
  withinMonthlyBudget: boolean;
  budgetMessage?: string;
}
/** Elastic (pay-per-use) shared detonation host runtime, from scaling.state.elastic. */
export interface ElasticHostView {
  enabled: boolean;
  allowOverBudget: boolean;
  lifecycleState: string;
  ec2State: string;
  upHoursToday: number;
  costTodayUsd: number;
  projectedMonthlyUsd: number;
  hourlyUsd: number;
  demand: { androidQueued: number; androidRunning: number; binaryQueued: number; binaryRunning: number };
  lastStartAt: string | null;
  lastStopAt: string | null;
  dayKey: string;
  updatedAt: string;
}
export interface ScalingStateView {
  reconciledAt?: string;
  fleet?: { usedGuests?: number; maxGlobalGuests?: number; spendTodayUsd?: number };
  services?: Record<string, { desired?: number; running?: number }>;
  elastic?: ElasticHostView;
  [key: string]: unknown;
}
export type ScalingPatch = Partial<{
  autoScaleEnabled: boolean;
  dailyBudgetUsd: number;
  monthlyBudgetUsd: number;
  detonationGuests: Partial<ScalingDimension>;
  detonationHosts: Partial<ScalingDimension>;
  analysisWorkers: Partial<ScalingDimension>;
  apiTasks: Partial<ScalingDimension>;
  openSearchNodes: Partial<ScalingDimension>;
  redisNodes: Partial<ScalingDimension>;
}>;

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  status: string;
  emailVerified: boolean;
  mfaEnabled: boolean;
  createdAt: string;
  orgs: { orgId: string; roles: string[]; status: string }[];
  apiKeys: number;
  dailyLookupQuota: number | null;
  effectiveDailyLookupQuota: number;
  limitsNote: string | null;
}

export interface SearchHit {
  type: string;
  id: string;
  attributes: {
    verdict: string;
    score?: number;
    family?: string;
    fileType: string;
    fileName?: string | null;
    visibility?: 'public' | 'private';
    /** Grammar-v2 columns (advanced search): engines flagged / total, size, first seen. */
    positives?: number;
    enginesTotal?: number;
    sizeBytes?: number;
    firstSeen?: string;
  };
}

/** Entity search hit (entity:domain|ip|url): one row per indicator aggregated across samples. */
export interface EntitySearchHit {
  type: 'domain' | 'ip' | 'url';
  id: string;
  attributes: {
    verdict: string;
    score: number;
    positives: number;
    relatedSamples: number;
    firstSeen: string;
    lastSeen: string;
    tags: string[];
    prevalence?: number;
    family?: string;
    sampleHashes: string[];
  };
}

export type IntelSearchHit = SearchHit | EntitySearchHit;

/** One resolved row from a multisearch paste. */
export interface MultisearchRow {
  indicator: string;
  kind: 'sha256' | 'sha1' | 'md5' | 'domain' | 'ipv4' | 'url';
  found: boolean;
  type?: string;
  sha256?: string;
  link?: string;
  graphNodeId?: string;
  attributes?: {
    verdict?: string;
    score?: number;
    family?: string;
    fileType?: string;
    fileName?: string | null;
    tags?: string[];
    firstSeen?: string;
    lastSeen?: string;
    prevalence?: number;
    positives?: number;
    enginesTotal?: number;
    sizeBytes?: number;
  };
}

export interface SubmitResponse {
  taskId: string;
  state: string;
  contentHash: string;
  verdict?: Verdict;
  reportUrl: string;
  pollUrl?: string;
  streamUrl?: string;
  queuePosition?: number;
  etaSeconds?: number;
}

/**
 * Mirrors `TaskState` in `platform/common/src/task.ts`, which is the state machine's source of
 * truth. It is mirrored rather than imported because `@threatlens/common` reaches for `node:crypto`
 * and does not belong in a browser bundle. Keep the two in step: adding a state there and not here
 * makes this union wrong, and the compiler cannot see across the gap.
 */
export type TaskState =
  | 'queued'
  | 'static'
  | 'queued-dynamic'
  | 'provisioning'
  | 'running'
  | 'analyzing'
  | 'enriching'
  | 'scored'
  | 'indexed'
  | 'completed'
  | 'failed'
  | 'timed-out'
  | 'cancelled';

/**
 * The analysis stopped and NO report will land from it. Distinct from FINISHED_TASK_STATES, which
 * also counts success - conflating the two is why three different definitions of "terminal" existed
 * across the SPA, each invisible to the compiler because `state` used to be a bare `string`.
 */
export const FAILED_TASK_STATES: ReadonlySet<TaskState> = new Set(['failed', 'timed-out', 'cancelled']);

/** The analysis is no longer queued or running, successfully or otherwise. */
export const FINISHED_TASK_STATES: ReadonlySet<TaskState> = new Set([
  'completed',
  'failed',
  'timed-out',
  'cancelled',
]);

export interface TaskStatus {
  taskId: string;
  state: TaskState;
  contentHash: string;
  queuePosition?: number;
  etaSeconds?: number;
  progress?: { stage: string; pct: number };
  staticVerdict?: Verdict;
  reportReady?: boolean;
  error?: string;
  visibility?: 'public' | 'private';
  provenance?: SourceProvenance;
  /** Magic-byte file type (pe/office-ooxml/pdf/elf/apk/...), set once static typing completes; used
   *  by the firehose file-type filter. Absent until the sample is typed. */
  fileType?: string;
}

export interface PartnerPreliminaryView {
  status: 'preliminary' | 'exists';
  verdict?: 'malicious' | 'suspicious' | 'unknown';
  enginesFlagged?: number;
  fileType?: string;
  sizeBytes?: number;
  detections?: { vendor: string; detection: string }[];
  deeperAnalysisInProgress?: boolean;
  message?: string;
}

export type PublicSampleSource =
  | 'malwarebazaar'
  | 'malwarebazaar-datalake'
  | 'urlhaus'
  | 'thezoo'
  | 'virussign'
  | 'malshare'
  | 'partner';

export function sourceLabel(source: string): string {
  switch (source) {
    case 'malwarebazaar':
    case 'malwarebazaar-datalake':
      return 'MalwareBazaar';
    case 'urlhaus':
      return 'URLhaus';
    case 'thezoo':
      return 'theZoo';
    case 'virussign':
      return 'VirusSign';
    case 'malshare':
      return 'MalShare';
    // Codename for the partner corpus (the real backing service is never surfaced).
    case 'partner':
      return 'Partner';
    default:
      return 'User';
  }
}

export interface SourceProvenance {
  source: PublicSampleSource;
  externalId: string;
  sourceUrl: string;
  originalUrl?: string;
  firstSeenAt: string;
  fetchedAt: string;
  tags: string[];
  malwareFamily?: string;
  expectedSha256?: string;
  hashVerified: boolean;
  terms: 'abuse.ch' | 'thezoo' | 'virussign' | 'malshare' | 'partner';
}

/** Run configuration for a submission (spec section L submission surface). */
export interface RunConfig {
  visibility: 'public' | 'private';
  network: 'full' | 'simulated' | 'none';
  interactive: boolean;
  /** Detonation controls (optional): force a guest OS or a specific sandbox engine (default: auto). */
  os?: 'auto' | 'windows' | 'linux' | 'android' | 'macos';
  engine?: string;
  /** Anonymous callers must pass a CAPTCHA token (dev token is `dev-captcha-ok`). */
  captchaToken?: string;
}

export interface ApiKeySummary {
  id: string;
  name: string;
  scope: string;
  last4: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** One row in a sample's submission history (visibility-scoped + submitter-redacted server-side). */
export interface SightingEntry {
  at: string;
  kind: 'analyzed' | 'reanalyzed' | 'ingested' | 'resubmitted';
  fileName: string | null;
  source: string | null;
  own: boolean;
  submitter: string;
}

export interface SightingsSummary {
  firstSeen: string | null;
  firstAnalyzed: string | null;
  lastSeen: string | null;
  submissionCount: number;
  distinctNameCount: number;
  recentNames: string[];
}

export interface FileHistory {
  data: SightingEntry[];
  summary: SightingsSummary;
}

/** Aggregate of every domain observed impersonating one brand, backing `/brand/<name>`. */
export interface BrandHubDomain {
  domain: string;
  scans: number;
  firstSeen: string | null;
  lastSeen: string | null;
  verdict: string;
  kits: string[];
  /** Claim sources that fired (domain-squat, phishing-kit, favicon, page-title) - the evidence chips. */
  sources?: string[];
  /** Squat kinds when the domain itself matched (exact-skeleton | typosquat | combosquat). */
  squatKinds?: string[];
  /** Example page for the compromised-host section, where the domain is NOT the deception. */
  examplePage?: string;
  exampleUuid?: string;
}

export interface BrandHub {
  brand: string;
  label: string;
  totalScans: number;
  domains: BrandHubDomain[];
  /** Domains where the DOMAIN ITSELF is the deception (squat evidence): lookalike domains. */
  lookalike?: BrandHubDomain[];
  /** Legitimate/third-party sites HOSTING brand-lure pages; the domain owner is usually a victim too. */
  hosted?: BrandHubDomain[];
  kits: { key: string; count: number }[];
  /** Honest scope note from the server: this counts what WE scanned, never the whole internet. */
  note: string;
}

export interface FamilyHub {
  family: string;
  total: number;
  firstSeen: string | null;
  lastSeen: string | null;
  fileTypes: { key: string; count: number }[];
  techniques: { key: string; count: number }[];
  c2s: { key: string; count: number }[];
  campaigns: { key: string; count: number }[];
  verdicts: { key: string; count: number }[];
  recent: {
    sha256: string;
    fileName: string | null;
    verdict: string;
    score: number;
    fileType: string;
    firstSeen: string;
    configFamily?: string;
  }[];
}
export interface GeneratedRuleView {
  rule: {
    name: string;
    tags?: string[];
    meta?: Record<string, string>;
    strings: { id: string; type: string; value: string; nocase?: boolean }[];
    condition: unknown;
  };
  yara: string;
  candidateStrings: number;
}

/** One row in the /scan "Recent public scans" list (public scans only, newest-first). */
export interface ScanRecentRow {
  uuid: string;
  url: string;
  hostname: string | null;
  verdict: string;
  score: number;
  malicious: boolean;
  time: string;
  title: string | null;
  /** How many malware samples communicate to this URL (0 when none). */
  correlatedSamples?: number;
  /** True when this scan was auto-triggered from a malware sample's extracted URLs. */
  fromMalware?: boolean;
}

/** A URL a malware sample communicates to (reverse correlation for the report page). */
/** Facet counts over the current scan-search query (present only when requested). */
export interface ScanSearchFacetsView {
  verdict: { key: string; count: number }[];
  impersonates: { key: string; count: number }[];
  kit: { key: string; count: number }[];
}

/** One row of the /brands directory. */
export interface BrandDirectory {
  brands: {
    brand: string;
    label: string;
    domains: number;
    flaggedDomains: number;
    scans: number;
    lastSeen: string | null;
    kits: string[];
  }[];
  note: string;
}

/**
 * Malware linked to a brand's impersonating domains, split BY EVIDENCE at the source. `attacks` are
 * runtime-observed or config-derived links; `references` are a hostname found as a STRING inside a
 * sample's bytes - not evidence of contact, and never counted as an attack.
 */
export interface BrandMalware {
  brand: string;
  label: string;
  domains: number;
  attacks: BrandMalwareRow[];
  references: BrandMalwareRow[];
  truncated: boolean;
  note: string;
}

export interface BrandMalwareRow {
  sampleSha256: string;
  sampleFamily: string | null;
  sampleName: string | null;
  domain: string;
  edgeKind: 'c2' | 'download' | 'config' | 'contacted' | 'referenced' | 'dns';
  hosting: boolean;
  scanUuid: string | null;
  firstSeen: string;
  lastSeen: string;
}

export interface SampleUrlRow {
  url: string;
  domain: string;
  edgeKind: 'c2' | 'download' | 'config' | 'contacted' | 'referenced' | 'dns';
  hosting: boolean;
  /** Only set when the scan actually resolves for this caller (never a dangling link). */
  scanUuid: string | null;
  scanUrl: string | null;
  scanStatus: 'queued' | 'running' | 'done' | 'error' | null;
  /** Verdict of this URL's own scan. `null` means NOT SCANNED (or unfinished) - never render it as clean. */
  verdict: 'malicious' | 'suspicious' | 'benign' | 'unknown' | null;
  score: number | null;
  /** The URL scanner's own malicious flag (false when the URL has not been scanned). */
  malicious: boolean;
  /** Brands this URL's scan EVIDENCED as impersonated (empty until a settled scan says so). */
  impersonates: string[];
  firstSeen: string;
  lastSeen: string;
}

export const api = {
  /** Approve a device-link code (right-click clients / extension / Outlook add-in sign-in). */
  deviceApprove: (userCode: string) =>
    req<{ ok: boolean; clientName: string }>(CONTROL, '/v1/auth/device/approve', {
      method: 'POST',
      body: JSON.stringify({ userCode }),
    }),
  register: (email: string, password: string, orgName?: string) =>
    req<{ userId: string; orgId: string }>(CONTROL, '/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, orgName }),
    }),
  login: (email: string, password: string, totp?: string) =>
    req<{ accessToken: string; refreshToken: string; orgId: string | null; userId: string }>(CONTROL, '/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, totp }),
    }),
  /** Passwordless sign-in: emails a 6-digit code. Always resolves, even for unknown addresses. */
  codeStart: (email: string) =>
    req<{ sent: boolean; retryAfterSeconds?: number }>(CONTROL, '/v1/auth/code/start', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  /** Exchanges the emailed code for a session. `created` is true when this call made the account. */
  codeVerify: (email: string, code: string, totp?: string) =>
    req<{
      accessToken: string;
      refreshToken: string;
      orgId: string | null;
      userId: string;
      created: boolean;
    }>(CONTROL, '/v1/auth/code/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code, totp }),
    }),
  /** Which social providers are configured right now (empty until an OAuth app is registered). */
  oauthProviders: () => req<{ providers: string[] }>(CONTROL, '/v1/auth/oauth/providers'),
  passkeyLoginBegin: (email: string) =>
    req<{ challenge: string; rpId: string; allowCredentials: string[] }>(CONTROL, '/v1/auth/passkey/login/begin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  passkeyLoginFinish: (payload: {
    email: string;
    credentialId: string;
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
  }) =>
    req<{ accessToken: string; refreshToken: string; orgId: string | null; userId: string }>(
      CONTROL,
      '/v1/auth/passkey/login/finish',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  /** Enrolls a passkey on the signed-in account (authenticated; used by the post-sign-in nudge). */
  passkeyRegisterBegin: () =>
    req<{ challenge: string; rpId: string; userId: string }>(CONTROL, '/v1/auth/webauthn/register/begin', {
      method: 'POST',
      body: '{}',
    }),
  passkeyRegisterFinish: (payload: {
    credentialId: string;
    clientDataJSON: string;
    publicKeyJwk: JsonWebKey;
    transports?: string[];
    label?: string;
  }) =>
    req<{ credentialId: string }>(CONTROL, '/v1/auth/webauthn/register/finish', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  requestPasswordReset: (email: string) =>
    req<{ ok: boolean }>(CONTROL, '/v1/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, password: string) =>
    req<{ ok: boolean }>(CONTROL, '/v1/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  confirmEmailChange: (token: string) =>
    req<{ changed: boolean }>(CONTROL, '/v1/auth/email/change/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  acceptInvitation: (token: string) =>
    req<{ orgId: string }>(CONTROL, '/v1/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  // API-key management (spec section D / Milestone C): keys are shown once, then only last4.
  listApiKeys: (orgId: string) =>
    req<{ keys: ApiKeySummary[] }>(CONTROL, `/v1/orgs/${orgId}/api-keys`),
  createApiKey: (orgId: string, name: string, scope: 'read-only' | 'submit' | 'admin') =>
    req<{ id: string; name: string; scope: string; last4: string; key: string; warning: string }>(
      CONTROL,
      `/v1/orgs/${orgId}/api-keys`,
      { method: 'POST', body: JSON.stringify({ name, scope }) },
    ),
  revokeApiKey: (orgId: string, keyId: string) =>
    req<{ revoked: boolean }>(CONTROL, `/v1/orgs/${orgId}/api-keys/${keyId}`, { method: 'DELETE' }),
  submitFile: (fileBase64: string, fileName: string, cfg: RunConfig) =>
    req<SubmitResponse>(ANALYSIS, '/v1/submit', {
      method: 'POST',
      body: JSON.stringify({ fileBase64, fileName, ...runConfigBody(cfg) }),
    }),
  submitUrl: (url: string, cfg: RunConfig) =>
    req<SubmitResponse>(ANALYSIS, '/v1/submit', {
      method: 'POST',
      body: JSON.stringify({ url, ...runConfigBody(cfg) }),
    }),
  // ---- URL scanning (urlscan.io superset) ----
  scanUrl: (input: {
    url: string;
    tier?: 'fast' | 'standard' | 'deep';
    visibility?: 'public' | 'unlisted' | 'private';
    grabFiles?: boolean;
    rescan?: boolean;
    captchaToken?: string;
  }) =>
    req<{ data: { uuid: string; status: string; cached?: boolean; resultUrl: string; streamUrl?: string; verdict?: string; score?: number } }>(
      ANALYSIS,
      '/v1/scan',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  scanResult: (uuid: string) => req<{ data: UrlScanResultView }>(ANALYSIS, `/v1/result/${encodeURIComponent(uuid)}`),
  scanSearch: (query: string, size = 20, opts?: { searchAfter?: (string | number)[]; facets?: boolean }) =>
    req<{
      results: UrlScanDocView[];
      total: number;
      has_more: boolean;
      /** Cursor for the next page; echo back via opts.searchAfter. */
      search_after?: (string | number)[];
      facets?: ScanSearchFacetsView;
      request_time_ms: number;
    }>(
      ANALYSIS,
      `/v1/urlscan/search?q=${encodeURIComponent(query)}&size=${size}` +
        (opts?.searchAfter ? `&search_after=${encodeURIComponent(JSON.stringify(opts.searchAfter))}` : '') +
        (opts?.facets ? '&facets=1' : ''),
    ),
  scanStreamUrl: (uuid: string) => `${ANALYSIS}/v1/result/${encodeURIComponent(uuid)}/stream`,
  scanStixUrl: (uuid: string) => `${ANALYSIS}/v1/result/${encodeURIComponent(uuid)}/stix`,
  /** Multi-space similar scans (favicon/DOM/screenshot/infra) - no paid vector DB required. */
  scanSimilar: (uuid: string, size = 12) =>
    req<{
      matches: { doc: UrlScanDocView; spaces: Record<string, number>; fused: number }[];
      spaces: string[];
      note?: string;
    }>(ANALYSIS, '/v1/urlscan/similar', { method: 'POST', body: JSON.stringify({ uuid, size }) }),
  maliciousFeed: (limit = 50) =>
    req<{ feed: { url: string; hostname: string; verdict: string; score: number; brands: string[]; kit?: string; time: string }[] }>(
      ANALYSIS,
      `/v1/feed/malicious?limit=${limit}`,
    ),
  /** Newest public scans for the /scan page history list. Unmetered (does not spend lookup quota). */
  scanRecent: (limit = 30) =>
    req<{ scans: ScanRecentRow[] }>(ANALYSIS, `/v1/urlscan/recent?limit=${limit}`),
  /** Bulk URL scan: a pasted list (`urls`) and/or freeform `text` (an uploaded file's content). */
  scanBatch: (input: { urls?: string[]; text?: string; tier?: 'fast' | 'standard'; visibility?: 'public' | 'private' }) =>
    req<{ data: { submitted: number; candidates: number; truncated: boolean; tier: string; visibility: string; jobs: { url: string; uuid: string; resultUrl: string }[] } }>(
      ANALYSIS,
      '/v1/scan/batch',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  /** Reverse correlation: the URLs a malware sample communicates to (report "Communicating URLs"). */
  filesUrls: (hash: string, limit = 100) =>
    req<{ data: SampleUrlRow[] }>(ANALYSIS, `/v1/files/${encodeURIComponent(hash)}/urls?limit=${limit}`),
  submitHash: (hash: string, cfg?: Partial<RunConfig>) =>
    req<SubmitResponse>(ANALYSIS, '/v1/submit', {
      method: 'POST',
      body: JSON.stringify({
        hash,
        visibility: cfg?.visibility,
        network: cfg?.network,
        captchaToken: cfg?.captchaToken,
      }),
    }),
  task: (taskId: string) =>
    req<TaskStatus>(ANALYSIS, `/v1/tasks/${encodeURIComponent(taskId)}`),
  firehose: (limit = 50) =>
    req<{ tasks: TaskStatus[]; running: number; maxRunning: number }>(
      ANALYSIS,
      `/v1/firehose/recent?limit=${limit}`,
    ),
  adminAnalysisStatus: () =>
    req<AdminAnalysisStatus>(ANALYSIS, '/v1/admin/analysis/status'),
  adminCancelTask: (taskId: string) =>
    req<{ cancelled: boolean }>(
      ANALYSIS,
      `/v1/admin/tasks/${encodeURIComponent(taskId)}/cancel`,
      { method: 'POST' },
    ),
  adminPauseAnalysis: () =>
    req<{ paused: boolean }>(ANALYSIS, '/v1/admin/analysis/pause', {
      method: 'POST',
    }),
  adminResumeAnalysis: () =>
    req<{ paused: boolean }>(ANALYSIS, '/v1/admin/analysis/resume', {
      method: 'POST',
    }),
  adminSources: () =>
    req<{ sources: SourceControl[]; stats: Record<string, number> }>(
      ANALYSIS,
      '/v1/admin/sources',
    ),
  adminUpdateSource: (source: string, patch: Partial<SourceControl>) =>
    req<SourceControl>(
      ANALYSIS,
      `/v1/admin/sources/${encodeURIComponent(source)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),
  adminPollSource: (source: string) =>
    req<{ results: unknown[] }>(
      ANALYSIS,
      `/v1/admin/sources/${encodeURIComponent(source)}/poll`,
      { method: 'POST' },
    ),
  adminQuarantine: () =>
    req<{ items: QuarantineItem[] }>(ANALYSIS, '/v1/admin/quarantine'),
  adminUpdateQuarantine: (id: string, status: string) =>
    req<QuarantineItem>(
      ANALYSIS,
      `/v1/admin/quarantine/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
    ),
  adminTakedown: (hash: string) =>
    req<{ takenDown: boolean }>(
      ANALYSIS,
      `/v1/admin/reports/${encodeURIComponent(hash)}/takedown`,
      { method: 'POST' },
    ),
  report: (hash: string) => req<ReportRecord>(ANALYSIS, `/v1/reports/${hash}`),
  publicReport: (hash: string) => req<PublicReport>(ANALYSIS, `/v1/public/reports/${hash}`),
  // Resolve any hash type (md5/sha1/sha256) to its canonical SHA-256 for a finished, visible sample.
  // Side-effect-free (unlike partnerLookup, which kicks off a scan); throws 404 if not in the corpus.
  resolveHash: (hash: string) =>
    req<{ data: { type: 'file'; id: string } }>(ANALYSIS, `/v1/files/${encodeURIComponent(hash)}`),
  // Per-hash submission history / names (first-seen, first-analyzed, every submission + reanalysis).
  // Unmetered; the report page loads it once. Server scopes visibility + redacts other tenants' submitters.
  fileHistory: (hash: string) => req<FileHistory>(ANALYSIS, `/v1/files/${hash}/history`),
  // Just-in-time preliminary intel when a searched hash isn't in our corpus yet: a verdict + the
  // detections from the AV engines we run, while a full detonation kicks off in the background.
  partnerLookup: (hash: string) =>
    req<{ data: { type: 'preliminary'; id: string; attributes: PartnerPreliminaryView } }>(
      ANALYSIS,
      `/v1/files/${encodeURIComponent(hash)}/partner-lookup`,
    ),
  // Family knowledge hub (Tier 4): aggregate one malware family across the corpus.
  familyHub: (name: string) => req<{ data: FamilyHub }>(ANALYSIS, `/v1/family/${encodeURIComponent(name)}`),
  // Impersonated-brand hub: the domains pretending to be one brand, deduplicated.
  brandHub: (name: string) => req<{ data: BrandHub }>(ANALYSIS, `/v1/brand/${encodeURIComponent(name)}`),
  /** The /brands directory: every brand with observed impersonation. Unmetered + server-cached. */
  brandsIndex: () => req<{ data: BrandDirectory }>(ANALYSIS, '/v1/brands'),
  /** Malware linked to a brand's impersonating domains, split by evidence strength at the source. */
  brandMalware: (name: string, limit = 100) =>
    req<{ data: BrandMalware }>(ANALYSIS, `/v1/brand/${encodeURIComponent(name)}/malware?limit=${limit}`),
  /** Hub export URL. CSV/JSON free; STIX/MISP use the daily allowance then credits. */
  brandExportUrl: (name: string, fmt: 'csv' | 'json' | 'stix' | 'misp') =>
    `${ANALYSIS}/v1/brand/${encodeURIComponent(name)}/export/${fmt}`,
  // Cross-corpus ATT&CK navigator (Tier 2): technique prevalence + trend + top families across the
  // corpus, with optional window/family/fileType/verdict lenses.
  attackNavigator: (params: { windowDays?: number; family?: string; fileType?: string; verdict?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.windowDays) qs.set('windowDays', String(params.windowDays));
    if (params.family) qs.set('family', params.family);
    if (params.fileType) qs.set('fileType', params.fileType);
    if (params.verdict) qs.set('verdict', params.verdict);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<{ data: AttackNavigatorData }>(ANALYSIS, `/v1/intelligence/attack${suffix}`);
  },
  // Generate a candidate YARA rule from a sample (Tier 5: detection engineering).
  generateRule: (hash: string) =>
    req<{ data: GeneratedRuleView }>(ANALYSIS, `/v1/files/${encodeURIComponent(hash)}/generate-rule`, {
      method: 'POST',
      body: '{}',
    }),
  // Track F: report collaboration (comments/votes/tags), PDF export, sample diff, attribution.
  reportComments: (hash: string) =>
    req<{ comments: ReportComment[]; votes: VoteTally; tags: { tag: string }[]; community?: CommunityOverlay }>(
      ANALYSIS,
      `/v1/files/${hash}/comments`,
    ),
  addReportComment: (hash: string, body: string) =>
    req<ReportComment>(ANALYSIS, `/v1/files/${hash}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  deleteReportComment: (hash: string, id: string) =>
    req<{ deleted: boolean }>(ANALYSIS, `/v1/files/${hash}/comments/${id}`, { method: 'DELETE' }),
  voteReport: (hash: string, vote: 'malicious' | 'harmless') =>
    req<VoteTally>(ANALYSIS, `/v1/files/${hash}/vote`, { method: 'POST', body: JSON.stringify({ vote }) }),
  addReportTag: (hash: string, tag: string) =>
    req<{ tags: { tag: string }[] }>(ANALYSIS, `/v1/files/${hash}/tags`, { method: 'POST', body: JSON.stringify({ tag }) }),
  /** The full downloadable analysis report. Free to everyone, including anonymous readers. */
  reportDocumentUrl: (hash: string, format: 'pdf' | 'docx') => `${ANALYSIS}/v1/files/${hash}/report.${format}`,
  scanDocumentUrl: (uuid: string, format: 'pdf' | 'docx') => `${ANALYSIS}/v1/result/${uuid}/report.${format}`,
  reportPdfUrl: (hash: string) => `${ANALYSIS}/v1/files/${hash}/report.pdf`,
  // Force a fresh reanalysis (all engines + a new detonation), overwriting the report. Requires a
  // signed-in account; returns the new task id to poll. Re-detonates, so it is metered like a submit.
  reanalyzeFile: (hash: string) =>
    req<{ data: { reanalyzed: boolean; taskId?: string; reason?: string; message?: string; reportUrl: string } }>(
      ANALYSIS,
      `/v1/files/${hash}/reanalyze`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  // Presigned download URL for a captured detonation artifact (pcap/memory/report/...).
  artifactUrl: async (hash: string, kind: string): Promise<string> => {
    const { data } = await req<{ data: string }>(
      ANALYSIS,
      `/v1/files/${encodeURIComponent(hash)}/artifacts/${encodeURIComponent(kind)}`,
    );
    return data;
  },
  reportAttribution: (hash: string) => req<Attribution>(ANALYSIS, `/v1/files/${hash}/attribution`),
  diffReports: (a: string, b: string) => req<ReportDiff>(ANALYSIS, `/v1/files/${a}/diff/${b}`),
  // Usage + quota + reward standing (Dashboard).
  usage: () => req<UsageInfo>(ANALYSIS, '/v1/usage'),
  mySubmissions: (limit = 50) => req<{ data: MySubmission[] }>(ANALYSIS, `/v1/me/submissions?limit=${limit}`),
  // Public platform status (Status page + integrations).
  platformStatus: () => req<PlatformStatus>(ANALYSIS, '/v1/status'),
  /** Windowed activity + verdict/source/family mixes for the Status page charts (default 24h). */
  statusHistory: (window: StatusWindow = '24h') =>
    req<StatusHistory>(ANALYSIS, `/v1/status/history?window=${window}`),

  // ---- Sample Vault (Phase 1: private single-owner). All routes require a verified account and are
  // gated on the sample_vault_enabled flag (from /v1/status.features.sampleVault). ----
  vaultList: (params: { limit?: number; cursor?: string; hash?: string; tag?: string; q?: string; verdict?: string; family?: string } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<{ data: VaultSampleView[]; meta: { limit: number; nextCursor: string | null } }>(ANALYSIS, `/v1/vault/samples${suffix}`);
  },
  vaultGet: (id: string) => req<{ data: VaultSampleView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}`),
  /** Store one small file inline (base64). Idempotent per hash; created=false on a re-upload. */
  vaultStore: (payload: { fileBase64: string; fileName?: string; notes?: string; source?: string; tags?: string[] }) =>
    req<{ data: VaultSampleView; created: boolean }>(ANALYSIS, '/v1/vault/samples', { method: 'POST', body: JSON.stringify(payload) }),
  /** Store a batch of small files inline; returns per-item results + a shared uploadBatchId. */
  vaultStoreBatch: (items: { fileBase64: string; fileName?: string; tags?: string[] }[]) =>
    req<{ data: { index: number; ok: boolean; id?: string; sha256?: string; created?: boolean; error?: string }[]; meta: { uploadBatchId: string; stored: number; total: number } }>(
      ANALYSIS,
      '/v1/vault/samples',
      { method: 'POST', body: JSON.stringify({ items }) },
    ),
  /** Enqueue ingestion of a completed large multipart upload (streamed by the worker). */
  vaultStoreUploaded: (uploadKey: string, fileName?: string) =>
    req<{ data: { accepted: boolean; ingestion: string } }>(ANALYSIS, '/v1/vault/samples', { method: 'POST', body: JSON.stringify({ uploadKey, fileName }) }),
  vaultDelete: (id: string) => req<{ data: { deleted: boolean } }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Enqueue a forced-private analysis of one owned sample. */
  vaultAnalyze: (id: string, settings?: { os?: string; engine?: string; network?: 'none' | 'full' }) =>
    req<{ data: { runId: string; taskId: string; contentHash: string; status: string } }>(
      ANALYSIS,
      `/v1/vault/samples/${encodeURIComponent(id)}/analyze`,
      { method: 'POST', body: JSON.stringify(settings ?? {}) },
    ),
  /** Batch analyze by ids[] (or analyze-all by batchId). */
  vaultAnalyzeBatch: (input: { ids?: string[]; batchId?: string; settings?: { os?: string; engine?: string; network?: 'none' | 'full' } }) =>
    req<{ data: { id: string; ok: boolean; runId?: string; taskId?: string; error?: string }[]; meta: { requested: number; enqueued: number } }>(
      ANALYSIS,
      '/v1/vault/analyze',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  vaultRuns: (id: string) => req<{ data: VaultRunDetail[] }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/runs`),
  vaultReport: (id: string, runId?: string) =>
    req<{ data: { status: string; runId: string; report: PublicReport | null } }>(
      ANALYSIS,
      `/v1/vault/samples/${encodeURIComponent(id)}/report${runId ? `?runId=${encodeURIComponent(runId)}` : ''}`,
    ),
  vaultUsage: () => req<{ data: VaultUsage }>(ANALYSIS, '/v1/vault/usage'),
  // Resumable multipart upload for large files (503 until the ingest worker is provisioned).
  vaultMultipartInit: (fileName: string) => req<{ data: VaultMultipartInit }>(ANALYSIS, '/v1/vault/uploads/multipart', { method: 'POST', body: JSON.stringify({ fileName }) }),
  vaultMultipartPartUrl: (key: string, uploadId: string, partNumber: number) =>
    req<{ data: { partNumber: number; url: string } }>(ANALYSIS, '/v1/vault/uploads/multipart/part', { method: 'POST', body: JSON.stringify({ key, uploadId, partNumber }) }),
  vaultMultipartParts: (key: string, uploadId: string) =>
    req<{ data: { key: string; uploadId: string; parts: { partNumber: number; etag: string; size: number }[] } }>(
      ANALYSIS,
      `/v1/vault/uploads/multipart/parts?key=${encodeURIComponent(key)}&uploadId=${encodeURIComponent(uploadId)}`,
    ),
  vaultMultipartComplete: (key: string, uploadId: string, parts: { partNumber: number; etag: string }[]) =>
    req<{ data: { uploadKey: string; size: number } }>(ANALYSIS, '/v1/vault/uploads/multipart/complete', { method: 'POST', body: JSON.stringify({ key, uploadId, parts }) }),

  // ---- Sample Vault (Phase 2: controlled collaboration). Sharing/visibility gated on
  // features.sampleVaultSharing; download gated on features.sampleVaultBinaryDownload. Sensitive ops
  // (publish, binary share, delete, download) require a step-up assertion in the X-Step-Up header. ----

  /** Step-up (recent re-auth) ceremony (control plane). Returns a short-lived assertion to present
   *  in the X-Step-Up header for sensitive vault ops. Provide password and/or a fresh MFA code. */
  authStepUp: (input: { password?: string; totp?: string }) =>
    req<StepUpResult>(CONTROL, '/v1/auth/step-up', { method: 'POST', body: JSON.stringify(input) }),

  /** Owner metadata edit (filename/notes/source/tags/collectionIds). */
  vaultPatchMeta: (id: string, patch: { originalFilename?: string; notes?: string; source?: string; tags?: string[]; collectionIds?: string[] }) =>
    req<{ data: VaultSampleView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  /** Per-dimension visibility patch (owner). Opening a dimension to 'public' needs a step-up. */
  vaultPatchVisibility: (id: string, patch: Partial<VaultVisibility>, stepUp?: string) =>
    req<{ data: { id: string; visibility: VaultVisibility } }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
      headers: stepUp ? { 'x-step-up': stepUp } : undefined,
    }),

  vaultListGrants: (id: string) => req<{ data: VaultGrantView[] }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/grants`),
  /** Create a named-user/workspace grant. downloadBinary in permissions requires a step-up. */
  vaultCreateGrant: (
    id: string,
    input: { granteeKind: 'user' | 'workspace'; granteeId: string; permissions: Partial<VaultPermissionSet>; expiresAt?: string },
    stepUp?: string,
  ) =>
    req<{ data: VaultGrantView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/grants`, {
      method: 'POST',
      body: JSON.stringify(input),
      headers: stepUp ? { 'x-step-up': stepUp } : undefined,
    }),
  vaultRevokeGrant: (id: string, grantId: string) =>
    req<{ data: VaultGrantView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/grants/${encodeURIComponent(grantId)}`, { method: 'DELETE' }),

  vaultListLinks: (id: string) => req<{ data: VaultLinkView[] }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/links`),
  /** Create a scoped share link. A download-scoped link needs a step-up at creation. Token returned once. */
  vaultCreateLink: (id: string, input: { scope: Partial<VaultPermissionSet>; expiresAt?: string; maxAccess?: number }, stepUp?: string) =>
    req<{ data: VaultLinkView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/links`, {
      method: 'POST',
      body: JSON.stringify(input),
      headers: stepUp ? { 'x-step-up': stepUp } : undefined,
    }),
  vaultRevokeLink: (id: string, linkId: string) =>
    req<{ data: VaultLinkView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}`, { method: 'DELETE' }),

  /** Resolve a bearer share link (metadata/report scope). No auth required. */
  vaultResolvePublic: (token: string) =>
    req<{ data: { sha256: string; originalFilename: string | null; tags: string[]; visibility: VaultVisibility; report?: PublicReport | null; access: VaultAccessSummary } }>(
      ANALYSIS,
      `/v1/public/vault/${encodeURIComponent(token)}`,
    ),

  vaultCollections: () => req<{ data: VaultCollectionView[] }>(ANALYSIS, '/v1/vault/collections'),
  vaultCreateCollection: (name: string, description?: string) =>
    req<{ data: VaultCollectionView }>(ANALYSIS, '/v1/vault/collections', { method: 'POST', body: JSON.stringify({ name, description }) }),

  /** Bulk action over selected sample ids. delete + binary share require a step-up. */
  vaultBulk: (
    input: { action: VaultBulkAction; ids: string[]; tags?: string[]; collectionId?: string; grant?: { granteeKind: 'user' | 'workspace'; granteeId: string; permissions: Partial<VaultPermissionSet>; expiresAt?: string }; settings?: { os?: string; engine?: string; network?: 'none' | 'full' } },
    stepUp?: string,
  ) =>
    req<{ data: { id: string; ok: boolean; error?: string; runId?: string; grantId?: string }[]; meta: { action: string; requested: number; succeeded: number } }>(
      ANALYSIS,
      '/v1/vault/bulk',
      { method: 'POST', body: JSON.stringify(input), headers: stepUp ? { 'x-step-up': stepUp } : undefined },
    ),

  /**
   * Controlled binary download. Requires ALL of: signed in, download authorization, a step-up
   * assertion, and the live-malware acknowledgement. Returns the password-protected archive as a Blob
   * (archive password is `infected`, also echoed in the X-Vault-Archive-Password header).
   */
  vaultDownload: async (id: string, opts: { stepUp: string; token?: string }): Promise<{ blob: Blob; filename: string; password: string }> => {
    await ensureFreshAccessToken();
    const headers: Record<string, string> = { 'content-type': 'application/json', 'x-step-up': opts.stepUp };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (tenantId) headers['x-tenant-id'] = tenantId;
    const res = await fetch(`${ANALYSIS}/v1/vault/samples/${encodeURIComponent(id)}/download`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ acknowledge: true, token: opts.token }),
    });
    if (!res.ok) {
      let detail: unknown;
      let message = res.statusText || `Download failed (${res.status})`;
      try {
        const j = (await res.json()) as { error?: { message?: string; details?: unknown } };
        message = j.error?.message ?? message;
        detail = j.error;
      } catch {
        // non-JSON error body
      }
      throw new ApiError(res.status, message, detail);
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') ?? '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const filename = m?.[1] ?? `${id}.7z`;
    const password = res.headers.get('x-vault-archive-password') ?? 'infected';
    return { blob, filename, password };
  },

  /**
   * Download a sample's bytes as a password-protected archive (password `infected`). Requires a
   * signed-in account + the implicit live-malware acknowledgement. `dropSha256` downloads a
   * runtime-dropped file captured during detonation instead of the primary sample.
   */
  downloadSampleArchive: async (
    hash: string,
    dropSha256?: string,
  ): Promise<{ blob: Blob; filename: string; password: string }> => {
    await ensureFreshAccessToken();
    const headers: Record<string, string> = {};
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (tenantId) headers['x-tenant-id'] = tenantId;
    const path = dropSha256
      ? `/v1/files/${encodeURIComponent(hash)}/dropped/${encodeURIComponent(dropSha256)}/download?ack=1`
      : `/v1/files/${encodeURIComponent(hash)}/download?ack=1`;
    const res = await fetch(`${ANALYSIS}${path}`, { method: 'GET', headers });
    if (!res.ok) {
      let detail: unknown;
      let message = res.statusText || `Download failed (${res.status})`;
      try {
        const j = (await res.json()) as { error?: { message?: string; details?: unknown } };
        message = j.error?.message ?? message;
        detail = j.error;
      } catch {
        // non-JSON error body
      }
      throw new ApiError(res.status, message, detail);
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') ?? '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const filename = m?.[1] ?? `${(dropSha256 ?? hash).slice(0, 16)}.7z`;
    const password = res.headers.get('x-archive-password') ?? 'infected';
    return { blob, filename, password };
  },

  // ---- Sample Vault (Phase 3: public-report publication pipeline). Owner routes are gated on
  // features.sampleVaultPublicReports; publish + confirm require a step-up assertion. Publication is
  // the source of truth for the public page - a report reaches the public ONLY through this flow. ----

  /** Create/refresh the sanitized DRAFT (not live). Requires a step-up assertion. */
  vaultPublish: (id: string, stepUp: string) =>
    req<{ data: VaultPublicationView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify({}),
      headers: { 'x-step-up': stepUp },
    }),
  /** The current publication for an owned sample (any status), with its sanitized projection. */
  vaultGetPublication: (id: string) =>
    req<{ data: VaultPublicationView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/publication`),
  /** Owner preview of the sanitized projection before it goes live (advances draft -> preview). */
  vaultPreviewPublication: (id: string) =>
    req<{ data: VaultPublicationView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/publication/preview`),
  /** Confirm (go live, or into review). Requires a step-up assertion. */
  vaultConfirmPublication: (id: string, stepUp: string) =>
    req<{ data: VaultPublicationView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/publication/confirm`, {
      method: 'POST',
      body: JSON.stringify({}),
      headers: { 'x-step-up': stepUp },
    }),
  /** Take the publication down (de-index + cache invalidation + restore visibility). No step-up. */
  vaultUnpublish: (id: string) =>
    req<{ data: VaultPublicationView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/unpublish`, { method: 'POST', body: JSON.stringify({}) }),
  /** Public: search/list PUBLISHED vault reports (anonymous, published-only). */
  vaultPublicReports: (params: { q?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<{ data: VaultPublicationSummary[]; meta: { count: number } }>(ANALYSIS, `/v1/public/vault/reports${suffix}`);
  },
  /** Public: the sanitized publication for a PUBLISHED hash (404 for anything else; no oracle). */
  vaultPublicReport: (hash: string) =>
    req<{ data: VaultPublicationSummary & { report: PublicReport } }>(ANALYSIS, `/v1/public/vault/reports/${encodeURIComponent(hash)}`),

  // ---- Sample Vault (Phase 4: community malware exchange). Owner routes + the community exchange are
  // gated on features.sampleVaultCommunityBinaries; download additionally on
  // features.sampleVaultVerifiedResearchersOnly. NO anonymous binary download in any code path. ----

  /** Contribute a sample's BINARY to the community corpus (step-up gated; enters moderation). */
  vaultContribute: (id: string, input: { title?: string; description?: string }, stepUp: string) =>
    req<{ data: CommunityContributionView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/contribute`, {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'x-step-up': stepUp },
    }),
  /** Owner: the current community contribution for a sample (any status) + moderation trail. */
  vaultGetContribution: (id: string) =>
    req<{ data: CommunityContributionView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/contribution`),
  /** Owner: withdraw an active contribution (kill switch; no step-up). */
  vaultWithdrawContribution: (id: string) =>
    req<{ data: CommunityContributionView }>(ANALYSIS, `/v1/vault/samples/${encodeURIComponent(id)}/contribution/withdraw`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  /** Owner: all my community contributions (any status). */
  vaultMyContributions: () => req<{ data: CommunityContributionView[] }>(ANALYSIS, '/v1/vault/community/contributions'),

  /** Eligible-audience: browse APPROVED community binaries (registered users; never anonymous). */
  communityBrowse: (params: { q?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<{ data: CommunityListingView[]; meta: { count: number; verifiedResearchersOnly: boolean; viewerVerified: boolean } }>(
      ANALYSIS,
      `/v1/vault/community${suffix}`,
    );
  },
  /** The caller's durable eligibility snapshot for a contributed sample (drives the remediation UI). */
  communityEligibility: (id: string) =>
    req<{ data: CommunityEligibility }>(ANALYSIS, `/v1/vault/community/${encodeURIComponent(id)}/eligibility`),
  /**
   * Community binary download through the FULL eligibility gate (email-verify, terms, verified
   * researcher when required, warning ack, step-up, rate-limit). Returns the password-protected 7z as
   * a Blob; on a precondition failure throws an ApiError whose detail carries the machine-readable
   * `reason` so the caller can prompt the exact remediation.
   */
  communityDownload: async (id: string, opts: { stepUp: string }): Promise<{ blob: Blob; filename: string; password: string }> => {
    await ensureFreshAccessToken();
    const headers: Record<string, string> = { 'content-type': 'application/json', 'x-step-up': opts.stepUp };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (tenantId) headers['x-tenant-id'] = tenantId;
    const res = await fetch(`${ANALYSIS}/v1/vault/community/${encodeURIComponent(id)}/download`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ acknowledge: true }),
    });
    if (!res.ok) {
      let detail: unknown;
      let message = res.statusText || `Download failed (${res.status})`;
      try {
        const j = (await res.json()) as { error?: { message?: string; details?: unknown } };
        message = j.error?.message ?? message;
        detail = j.error;
      } catch {
        // non-JSON error body
      }
      throw new ApiError(res.status, message, detail);
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') ?? '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const filename = m?.[1] ?? `${id}.7z`;
    const password = res.headers.get('x-vault-archive-password') ?? 'infected';
    return { blob, filename, password };
  },

  // Terms (ToS / AUP) + versioned acceptance.
  getTerms: () => req<{ data: { version: string; documents: TermsDoc[] } }>(ANALYSIS, '/v1/terms'),
  getMyTerms: () => req<{ data: MyTermsStatus }>(ANALYSIS, '/v1/me/terms'),
  acceptTerms: (version: string) =>
    req<{ data: { accepted: boolean; version: string; acceptedAt: string } }>(ANALYSIS, '/v1/terms/accept', {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),

  // Verified-researcher application + self status.
  applyResearcher: (statement: string) =>
    req<{ data: { status: ResearcherVerificationStatus; alreadyVerified?: boolean } }>(ANALYSIS, '/v1/vault/community/verify/apply', {
      method: 'POST',
      body: JSON.stringify({ statement }),
    }),
  getMyResearcher: () => req<{ data: MyResearcherStatus }>(ANALYSIS, '/v1/me/researcher'),

  // Platform-admin: community moderation queue, verified-researcher management, abuse controls.
  adminCommunityModeration: (limit = 100) =>
    req<{ data: CommunityModerationItem[]; meta: { count: number } }>(ANALYSIS, `/v1/admin/community/moderation?limit=${limit}`),
  adminModerate: (id: string, decision: 'approve' | 'reject' | 'request-changes', body: { note?: string } = {}) =>
    req<{ data: CommunityModerationItem }>(ANALYSIS, `/v1/admin/community/moderation/${encodeURIComponent(id)}/${decision}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminTakedownContribution: (id: string, reason?: string) =>
    req<{ data: CommunityModerationItem }>(ANALYSIS, `/v1/admin/community/contributions/${encodeURIComponent(id)}/takedown`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  adminCommunityResearchers: (status?: ResearcherVerificationStatus) =>
    req<{ data: ResearcherVerificationView[] }>(ANALYSIS, `/v1/admin/community/researchers${status ? `?status=${status}` : ''}`),
  adminResearcherDecision: (userId: string, action: 'grant' | 'reject' | 'revoke', note?: string) =>
    req<{ data: ResearcherVerificationView }>(ANALYSIS, `/v1/admin/community/researchers/${encodeURIComponent(userId)}/${action}`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  adminCommunityAccess: (params: { suspended?: boolean; flagged?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (params.suspended) qs.set('suspended', '1');
    if (params.flagged) qs.set('flagged', '1');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<{ data: CommunityAccessStateView[] }>(ANALYSIS, `/v1/admin/community/access${suffix}`);
  },
  adminSuspendCommunity: (userId: string, reason: string) =>
    req<{ data: CommunityAccessStateView }>(ANALYSIS, `/v1/admin/community/access/${encodeURIComponent(userId)}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  adminRestoreCommunity: (userId: string) =>
    req<{ data: CommunityAccessStateView }>(ANALYSIS, `/v1/admin/community/access/${encodeURIComponent(userId)}/restore`, { method: 'POST', body: JSON.stringify({}) }),
  adminClearCommunityFlag: (userId: string) =>
    req<{ data: CommunityAccessStateView }>(ANALYSIS, `/v1/admin/community/access/${encodeURIComponent(userId)}/clear-flag`, { method: 'POST', body: JSON.stringify({}) }),
  /** Live-presence heartbeat: marks the caller online (registered if signed in, else anonymous by
   * visitorId) and returns current online counts. Unmetered; safe to call every ~45s from any page. */
  presencePing: (visitorId: string) =>
    req<{ online: OnlineCounts }>(ANALYSIS, '/v1/presence', { method: 'POST', body: JSON.stringify({ visitorId }) }),
  /** Partner program: apply to list an AV/EDR/CTI product as a scanning engine (public, captcha-gated). */
  submitPartnerApplication: (payload: {
    company: string;
    product: string;
    category: string;
    contactName: string;
    email: string;
    message?: string;
    captchaToken: string;
  }) => req<{ ok: boolean }>(ANALYSIS, '/v1/partners/apply', { method: 'POST', body: JSON.stringify(payload) }),
  // Super-admin console (platform-admin only).
  adminListUsers: (search = '', limit = 100) =>
    req<{ data: AdminUser[] }>(CONTROL, `/v1/admin/users?search=${encodeURIComponent(search)}&limit=${limit}`),
  adminGetUser: (id: string) => req<AdminUser>(CONTROL, `/v1/admin/users/${encodeURIComponent(id)}`),
  adminUpdateUser: (id: string, patch: { status?: string; orgId?: string; roles?: string[] }) =>
    req<AdminUser>(CONTROL, `/v1/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  adminSetUserLimits: (id: string, dailyLookupQuota: number | null, note?: string | null) =>
    req<{ userId: string; dailyLookupQuota: number | null }>(CONTROL, `/v1/admin/users/${encodeURIComponent(id)}/limits`, {
      method: 'PUT',
      body: JSON.stringify({ dailyLookupQuota, note }),
    }),
  adminRevokeSessions: (id: string) => req<{ ok: boolean }>(CONTROL, `/v1/admin/users/${encodeURIComponent(id)}/revoke-sessions`, { method: 'POST' }),
  adminRevokeKeys: (id: string) => req<{ revoked: number }>(CONTROL, `/v1/admin/users/${encodeURIComponent(id)}/revoke-keys`, { method: 'POST' }),
  adminResetMfa: (id: string) => req<{ ok: boolean }>(CONTROL, `/v1/admin/users/${encodeURIComponent(id)}/reset-mfa`, { method: 'POST' }),
  adminGetPlatformSettings: () => req<{ settings: Record<string, string> }>(CONTROL, '/v1/admin/platform-settings'),
  adminSetPlatformSettings: (settings: Record<string, string | number | boolean>) =>
    req<{ settings: Record<string, string> }>(CONTROL, '/v1/admin/platform-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  // ---- MalwareAnalyzer Agent + token wallet + billing (Workstream J/K) ----
  getWallet: () => req<{ wallet: Wallet }>(CONTROL, '/v1/wallet'),
  setAutoRecharge: (enabled: boolean, pack?: TokenPackId | null) =>
    req<{ wallet: Wallet }>(CONTROL, '/v1/wallet/auto-recharge', { method: 'PUT', body: JSON.stringify({ enabled, pack }) }),
  agentQuery: (query: string, mode: 'quick' | 'advanced') =>
    req<AgentResponse>(ANALYSIS, '/v1/agent/query', { method: 'POST', body: JSON.stringify({ query, mode }) }),
  /**
   * Streaming Agent query (SSE). Advanced mode runs Atlas's own grounded-reasoning pass server-side
   * (~40s) without hitting the edge timeout; `onStatus` surfaces progress, and the resolved value is
   * the final answer (with the FULL token usage, including Atlas's real tokens).
   */
  agentQueryStream: async (
    query: string,
    mode: 'quick' | 'advanced',
    onStatus?: (stage: string) => void,
  ): Promise<AgentResponse> => {
    // `authorizedFetch`, not a hand-rolled fetch: this is a POST, so in cookie mode it needs the
    // CSRF header or the global guard rejects it with 403 before the route is reached. It did, and
    // because only Advanced mode streams while Quick mode goes through `req()`, the widget appeared
    // intermittently broken rather than plainly broken.
    const res = await authorizedFetch(`${ANALYSIS}/v1/agent/stream`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ query, mode }),
    });
    if (!res.ok || !res.body) {
      let message = `Agent error ${res.status}`;
      let detail: unknown;
      try {
        const j = (await res.json()) as { error?: { message?: string } };
        if (j?.error?.message) message = j.error.message;
        detail = j?.error;
      } catch {
        // non-JSON error body; keep the status-based message
      }
      throw new ApiError(res.status, message, detail);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let final: AgentResponse | null = null;
    let streamErr: { message: string; detail?: unknown } | null = null;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const frames = buf.split('\n\n');
      buf = frames.pop() ?? '';
      for (const frame of frames) {
        let ev = 'message';
        let data = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) ev = line.slice(6).trim();
          else if (line.startsWith('data:')) data += line.slice(5).trim();
          // lines starting with ':' are keepalive comments - ignore
        }
        if (!data) continue;
        let parsed: unknown;
        try {
          parsed = JSON.parse(data);
        } catch {
          continue;
        }
        if (ev === 'status') onStatus?.((parsed as { stage?: string }).stage ?? '');
        else if (ev === 'done') final = parsed as AgentResponse;
        else if (ev === 'error') streamErr = { message: (parsed as { message?: string }).message ?? 'Agent error' };
      }
    }
    if (streamErr) throw new ApiError(200, streamErr.message, streamErr.detail);
    if (!final) throw new ApiError(200, 'Agent stream ended without a result');
    return final;
  },
  // Scheduled Agent queries + run history.
  listSchedules: () => req<{ schedules: AgentSchedule[] }>(CONTROL, '/v1/agent/schedules'),
  createSchedule: (name: string, query: string, mode: 'quick' | 'advanced', intervalMinutes: number) =>
    req<{ schedule: AgentSchedule }>(CONTROL, '/v1/agent/schedules', {
      method: 'POST',
      body: JSON.stringify({ name, query, mode, intervalMinutes }),
    }),
  deleteSchedule: (id: string) => req<{ deleted: boolean }>(CONTROL, `/v1/agent/schedules/${id}`, { method: 'DELETE' }),
  agentRuns: (limit = 20) => req<{ runs: AgentRun[] }>(CONTROL, `/v1/agent/runs?limit=${limit}`),

  // ---- Tier 1 retention engine: Watches / Notifications / Hunts ----
  listWatches: () => req<{ data: Watch[] }>(ANALYSIS, '/v1/watches'),
  addWatch: (kind: WatchKind, value: string, label?: string) =>
    req<Watch>(ANALYSIS, '/v1/watches', { method: 'POST', body: JSON.stringify({ kind, value, label }) }),
  removeWatch: (id: string) => req<{ removed: boolean }>(ANALYSIS, `/v1/watches/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  notifications: (limit = 30, unread = false) =>
    req<{ data: NotificationItem[]; unread: number }>(ANALYSIS, `/v1/notifications?limit=${limit}${unread ? '&unread=1' : ''}`),
  markNotificationsRead: (ids?: string[]) =>
    req<{ marked: number }>(ANALYSIS, '/v1/notifications/read', { method: 'POST', body: JSON.stringify(ids && ids.length ? { ids } : { all: true }) }),
  notificationPrefs: () => req<NotificationPrefs>(ANALYSIS, '/v1/notifications/prefs'),
  setNotificationPrefs: (patch: Partial<NotificationPrefs>) =>
    req<NotificationPrefs>(ANALYSIS, '/v1/notifications/prefs', { method: 'PUT', body: JSON.stringify(patch) }),
  listHunts: () => req<{ data: StandingHunt[] }>(ANALYSIS, '/v1/hunts'),
  createHunt: (name: string, rule: unknown) =>
    req<{ huntId: string; status: string; scanned: number; matches: HuntMatch[]; standing: boolean }>(ANALYSIS, '/v1/retrohunt', {
      method: 'POST',
      body: JSON.stringify({ name, rule, standing: true }),
    }),
  runRetrohunt: (name: string, rule: unknown) =>
    req<{ huntId: string; status: string; scanned: number; matches: HuntMatch[]; standing: boolean }>(ANALYSIS, '/v1/retrohunt', {
      method: 'POST',
      body: JSON.stringify({ name, rule, standing: false }),
    }),
  deleteHunt: (id: string) => req<{ removed: boolean }>(ANALYSIS, `/v1/hunts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Tier 5: community rule library / marketplace (publish / browse / fork YARA + Sigma rules).
  listRules: (limit = 60) => req<{ data: PublicRule[] }>(ANALYSIS, `/v1/rules?limit=${limit}`),
  myRules: () => req<{ data: PublicRule[] }>(ANALYSIS, '/v1/rules/mine'),
  getRule: (id: string) => req<PublicRule>(ANALYSIS, `/v1/rules/${encodeURIComponent(id)}`),
  publishRule: (input: { name: string; description?: string; format: RuleFormat; rule: unknown; tags?: string[] }) =>
    req<PublicRule>(ANALYSIS, '/v1/rules', { method: 'POST', body: JSON.stringify(input) }),
  forkRule: (id: string) => req<PublicRule>(ANALYSIS, `/v1/rules/${encodeURIComponent(id)}/fork`, { method: 'POST' }),
  deleteRule: (id: string) => req<{ removed: boolean }>(ANALYSIS, `/v1/rules/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Tier 3: campaign timeline (chronological view of a campaign's samples + infra).
  campaign: (id: string, depth = 2) => req<CampaignGraph>(ANALYSIS, `/v1/campaigns/${encodeURIComponent(id)}?depth=${depth}`),
  tokenPacks: () => req<{ packs: TokenPack[] }>(ANALYSIS, '/v1/billing/token-packs'),
  tokenCheckout: (pack: TokenPackId) =>
    req<{ url: string; sessionId: string; pack: TokenPack }>(ANALYSIS, '/v1/billing/tokens/checkout', {
      method: 'POST',
      body: JSON.stringify({ pack }),
    }),
  // ---- Subscription plans (Community/Pro/Team) ----
  billingPlans: () => req<{ plans: Record<'free' | 'hunter' | 'enterprise', PlanView> }>(ANALYSIS, '/v1/billing/plans'),
  subscribeCheckout: (plan: 'hunter' | 'enterprise', interval: 'month' | 'year') =>
    req<{ url: string; sessionId: string; plan: string; interval: string; amountUsd: number }>(
      ANALYSIS,
      '/v1/billing/subscribe/checkout',
      { method: 'POST', body: JSON.stringify({ plan, interval }) },
    ),
  billingPortal: () =>
    req<{ url: string }>(ANALYSIS, '/v1/billing/portal', { method: 'POST', body: JSON.stringify({}) }),
  // ---- Growth loop: referrals + attribution (drives adoption) ----
  referralCode: () => req<ReferralInfo>(GROWTH, '/v1/growth/referral-code', { method: 'POST', body: JSON.stringify({}) }),
  referralConvert: (code: string, refereeDeviceId?: string) =>
    req<{ decision: string; reasons: string[]; rewarded: boolean }>(GROWTH, '/v1/growth/convert', {
      method: 'POST',
      body: JSON.stringify({ code, refereeDeviceId }),
    }),
  attributionSignup: (source: string, medium?: string, campaign?: string) =>
    req<{ ok: boolean }>(GROWTH, '/v1/growth/attribution/signup', {
      method: 'POST',
      body: JSON.stringify({ source, medium, campaign }),
    }),
  attributionActivity: (kind: 'submit' | 'share' | 'login' | 'convert') =>
    req<{ ok: boolean; referrerRewarded?: boolean }>(GROWTH, '/v1/growth/attribution/activity', {
      method: 'POST',
      body: JSON.stringify({ kind }),
    }),
  // Admin Growth panel (platform-admin JWT accepted by growth-svc analytics endpoints).
  growthFunnel: () => req<GrowthFunnel>(GROWTH, '/v1/growth/funnel'),
  growthSources: () => req<Record<string, number>>(GROWTH, '/v1/growth/analytics/sources'),
  growthCohorts: () => req<{ cohorts: GrowthCohort[] }>(GROWTH, '/v1/growth/analytics/cohorts'),
  // Admin: set/increase a user's token balance.
  adminSetUserTokens: (userId: string, patch: { freeGranted?: number; credit?: number }) =>
    req<{ wallet: { freeGranted: number; purchased: number; used: number; remaining: number } }>(
      CONTROL,
      `/v1/admin/users/${userId}/tokens`,
      { method: 'PUT', body: JSON.stringify(patch) },
    ),
  // Admin-driven scaling + live cost (Infrastructure & Cost tab).
  adminGetScaling: () => req<{ config: ScalingConfig; cost: CostBreakdown }>(CONTROL, '/v1/admin/scaling'),
  adminScalingState: () => req<{ config: ScalingConfig; state: ScalingStateView | null; cost: CostBreakdown & { actual?: unknown } }>(CONTROL, '/v1/admin/scaling/state'),
  adminScalingPreview: (patch: ScalingPatch) =>
    req<CostPreview>(CONTROL, '/v1/admin/scaling/preview', { method: 'POST', body: JSON.stringify(patch) }),
  adminApplyScaling: (patch: ScalingPatch) =>
    req<{ config: ScalingConfig; cost: CostBreakdown }>(CONTROL, '/v1/admin/scaling', { method: 'PUT', body: JSON.stringify(patch) }),
  // Intelligence search (public corpus + the caller's private reports when authenticated).
  // Grammar v2: entity:file|domain|ip|url + p:/size:/fs:/ls:/engines:/... structured filters.
  // `searchAfter` is the opaque cursor echoed from a prior response's `meta.search_after`. The API has
  // always paged (it returns `total`, `has_more` and the cursor), but this client declared neither the
  // cursor nor the total, so the UI silently showed the first 100 of what was often tens of thousands
  // of matches with no way to reach the rest.
  searchIntel: (query: string, searchAfter?: unknown[]) =>
    req<{
      data: IntelSearchHit[];
      meta: {
        count: number;
        total?: number;
        has_more?: boolean;
        search_after?: unknown[];
        entity?: string;
        engine?: string;
      };
    }>(
      ANALYSIS,
      `/v1/intelligence/search?query=${encodeURIComponent(query)}${
        searchAfter ? `&search_after=${encodeURIComponent(JSON.stringify(searchAfter))}` : ''
      }`,
    ),
  // Multisearch: paste any text; every recognizable hash/domain/IP/URL is extracted + resolved.
  multisearch: (input: string) =>
    req<{ data: MultisearchRow[]; meta: { requested: number; found: number; capped: boolean } }>(
      ANALYSIS,
      '/v1/intelligence/multisearch',
      { method: 'POST', body: JSON.stringify({ input }) },
    ),
  // Find similar samples (VT signature pivot: imphash/vhash exact + ctph/structural fuzzy).
  similar: (hash: string, limit = 20) =>
    req<{ data: SimilarSample[]; meta: { count: number } }>(
      ANALYSIS,
      `/v1/files/${encodeURIComponent(hash)}/similar?limit=${limit}`,
    ),
  // Content / byte search over the corpus (VT Grep).
  grep: (pattern: string, kind: 'string' | 'hex' | 'regex' = 'string') =>
    req<{ data: { id: string; attributes: { fileName: string | null; fileType: string; snippet: string } }[]; meta: { scanned: number; count: number } }>(
      ANALYSIS,
      `/v1/intelligence/grep?pattern=${encodeURIComponent(pattern)}&kind=${kind}`,
    ),
  // Collections / investigations (VT Collections + case management).
  listCollections: () => req<{ data: CollectionSummary[] }>(ANALYSIS, '/v1/collections'),
  getCollection: (id: string) => req<CollectionDetail>(ANALYSIS, `/v1/collections/${encodeURIComponent(id)}`),
  publicCollection: (idOrToken: string) => req<CollectionDetail>(ANALYSIS, `/v1/public/collections/${encodeURIComponent(idOrToken)}`),
  createCollection: (input: { name: string; description?: string; visibility?: 'public' | 'private'; sampleHashes?: string[] }) =>
    req<CollectionDetail>(ANALYSIS, '/v1/collections', { method: 'POST', body: JSON.stringify(input) }),
  addToCollection: (id: string, items: { sampleHashes?: string[]; iocs?: { type: string; value: string }[]; note?: string }) =>
    req<CollectionDetail>(ANALYSIS, `/v1/collections/${encodeURIComponent(id)}/items`, { method: 'POST', body: JSON.stringify(items) }),
  // Owner-driven sharing: flip a report's visibility. Returns the shareable URL when made public.
  setReportVisibility: (hash: string, visibility: 'public' | 'private') =>
    req<{ contentHash: string; visibility: string; shareUrl: string | null }>(
      ANALYSIS,
      `/v1/reports/${encodeURIComponent(hash)}`,
      { method: 'PATCH', body: JSON.stringify({ visibility }) },
    ),
  reputation: (indicator: string) =>
    req<ReputationResult>(ANALYSIS, `/v1/reputation/${encodeURIComponent(indicator)}`),
  communityFeed: () => req<{ items: FeedItem[] }>(ANALYSIS, '/v1/community/feed'),
  trends: () => req<Trends>(ANALYSIS, '/v1/community/trends'),
  // Researcher Rank: durable contribution leaderboard (window all/weekly/monthly), the caller's own
  // rank/streak/tier (dashboard widget), and a public researcher profile by handle (or user id).
  leaderboard: (window: LeaderboardWindow = 'all', limit = 25) =>
    req<{ leaderboard: LeaderboardEntry[]; window: LeaderboardWindow }>(
      ANALYSIS,
      `/v1/community/leaderboard?window=${window}&limit=${limit}`,
    ),
  myRank: () => req<MyRank>(ANALYSIS, '/v1/me/rank'),
  researcher: (handleOrId: string) => req<ResearcherProfile>(ANALYSIS, `/v1/researchers/${encodeURIComponent(handleOrId)}`),
  verifyEmail: (token: string) =>
    req<{ verified: boolean; joinedOrgId?: string | null }>(CONTROL, '/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  graphPivot: (nodeId: string, depth = 1) =>
    req<GraphPivot>(ANALYSIS, `/v1/graph/pivot/${encodeURIComponent(nodeId)}?depth=${depth}`),
  // Batch pivot: seed the investigation graph from many IOCs at once.
  graphPivotBatch: (seeds: string[], depth = 1) =>
    req<GraphPivotBatch>(ANALYSIS, '/v1/graph/pivot', {
      method: 'POST',
      body: JSON.stringify({ seeds, depth }),
    }),
  // Extract IOCs from an uploaded document (PDF/JSON/CSV/STIX/MISP/eml/archive/...).
  extractIocs: (fileBase64: string, fileName: string) =>
    req<{ data: IocExtractionResult }>(ANALYSIS, '/v1/extract/iocs', {
      method: 'POST',
      body: JSON.stringify({ fileBase64, fileName }),
    }),
  // Investigations (CTI case management for the graph workbench).
  listInvestigations: () => req<{ data: InvestigationSummary[] }>(ANALYSIS, '/v1/investigations'),
  getInvestigation: (id: string) =>
    req<InvestigationDetail>(ANALYSIS, `/v1/investigations/${encodeURIComponent(id)}`),
  publicInvestigation: (idOrToken: string) =>
    req<InvestigationDetail>(ANALYSIS, `/v1/public/investigations/${encodeURIComponent(idOrToken)}`),
  createInvestigation: (input: {
    name: string;
    description?: string;
    visibility?: 'public' | 'private';
    seeds?: string[];
    tags?: string[];
    graph?: InvestigationGraph;
  }) => req<InvestigationDetail>(ANALYSIS, '/v1/investigations', { method: 'POST', body: JSON.stringify(input) }),
  updateInvestigation: (
    id: string,
    patch: {
      name?: string;
      description?: string;
      visibility?: 'public' | 'private';
      seeds?: string[];
      tags?: string[];
      graph?: InvestigationGraph;
    },
  ) =>
    req<InvestigationDetail>(ANALYSIS, `/v1/investigations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  addInvestigationNote: (id: string, note: string) =>
    req<InvestigationDetail>(ANALYSIS, `/v1/investigations/${encodeURIComponent(id)}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  deleteInvestigation: (id: string) =>
    req<{ deleted: boolean }>(ANALYSIS, `/v1/investigations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Export a saved investigation (png/svg/pdf/stix/misp/csv/graphml/json). Returns a Blob to download.
  exportInvestigation: async (id: string, fmt: string): Promise<Blob> => {
    // This used to read `getSession().accessToken` and send it as a Bearer. In cookie mode that
    // value is the SENTINEL STRING 'cookie', not a token, and the server prefers a Bearer over the
    // cookie whenever one is present - so it authenticated nobody, and the valid session cookie was
    // never consulted. The user saw "Investigation not found" on a case they had open, or a
    // "Sign in" upsell while signed in. `authorizedFetch` sends the real credentials.
    const res = await authorizedFetch(
      `${ANALYSIS}/v1/investigations/${encodeURIComponent(id)}/export/${encodeURIComponent(fmt)}`,
    );
    if (!res.ok) {
      // Surface the structured error envelope (e.g. a 402 premium-export charge) so the UI can
      // show the honest upsell instead of a bare status code.
      let message = `Export failed (${res.status})`;
      let detail: unknown;
      try {
        const j = (await res.json()) as { error?: { message?: string } };
        if (j?.error?.message) message = j.error.message;
        detail = j?.error;
      } catch {
        // non-JSON body; keep the status-based message
      }
      throw new ApiError(res.status, message, detail);
    }
    return res.blob();
  },
  // BYO enrichment API keys (stored encrypted; only last4 returned).
  listEnrichmentKeys: () =>
    req<{ data: EnrichmentKeyView[]; providers: string[] }>(ANALYSIS, '/v1/settings/enrichment-keys'),
  setEnrichmentKey: (provider: string, apiKey: string) =>
    req<EnrichmentKeyView>(ANALYSIS, `/v1/settings/enrichment-keys/${encodeURIComponent(provider)}`, {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    }),
  deleteEnrichmentKey: (provider: string) =>
    req<{ deleted: boolean }>(ANALYSIS, `/v1/settings/enrichment-keys/${encodeURIComponent(provider)}`, {
      method: 'DELETE',
    }),
  testEnrichmentKey: (provider: string) =>
    req<{ ok: boolean; found?: boolean; error?: string }>(
      ANALYSIS,
      `/v1/settings/enrichment-keys/${encodeURIComponent(provider)}/test`,
      { method: 'POST' },
    ),
  enrichmentProviderCatalog: () =>
    req<{ providers: EnrichmentProviderMeta[]; configured: string[] }>(ANALYSIS, '/v1/enrichment/providers'),
  enrichNodes: (nodes: EnrichNodeInput[], providers: string[]) =>
    req<GraphEnrichResponse>(ANALYSIS, '/v1/graph/enrich', {
      method: 'POST',
      body: JSON.stringify({ nodes, providers }),
    }),
  // Interactive live-VM sessions (spec section H).
  startSession: (opts: StartSessionOptions = {}) =>
    req<SessionInfo>(INTERACTIVE, '/v1/sessions', {
      method: 'POST',
      body: JSON.stringify({
        os: opts.os ?? 'linux',
        arch: opts.arch ?? 'x86_64',
        network: opts.network ?? 'simulated',
        fileBase64: opts.fileBase64,
        fileName: opts.fileName,
      }),
    }),
  getSession: (id: string) => req<SessionInfo>(INTERACTIVE, `/v1/sessions/${id}`),
  sessionControl: (id: string, action: Record<string, unknown>) =>
    req<Record<string, unknown>>(INTERACTIVE, `/v1/sessions/${id}/control`, { method: 'POST', body: JSON.stringify(action) }),
  sessionWebrtcConfig: (id: string) =>
    req<WebrtcConfig>(INTERACTIVE, `/v1/sessions/${id}/webrtc`),
  startAutopilot: (id: string, goal?: string) =>
    req<{ started: boolean; goal: string }>(INTERACTIVE, `/v1/sessions/${id}/autopilot`, {
      method: 'POST',
      body: JSON.stringify({ goal }),
    }),
  sessionReplay: (id: string) =>
    req<{ recording: SessionRecording; shareToken: string }>(INTERACTIVE, `/v1/sessions/${id}/replay`),
  publicReplay: (token: string) =>
    req<{ recording: SessionRecording }>(INTERACTIVE, `/v1/replay/${encodeURIComponent(token)}`),
  endSession: (id: string) => req<{ vmMinutes: number; shareToken?: string }>(INTERACTIVE, `/v1/sessions/${id}`, { method: 'DELETE' }),
};

export interface RecordedEvent {
  atMs: number;
  kind: 'lifecycle' | 'control' | 'behavior' | 'frame';
  payload: Record<string, unknown>;
}

export interface SessionRecording {
  sessionId: string;
  tenantId?: string;
  startedAt: string;
  endedAt?: string;
  durationMs: number;
  events: RecordedEvent[];
}

export interface RoomPresence {
  driverId: string | null;
  participants: { id: string; name: string; role: 'driver' | 'observer'; cursor?: { x: number; y: number } }[];
}

export interface WebrtcConfig {
  transport: string;
  encoder: string;
  iceServers: { urls: string[]; username?: string; credential?: string }[];
  bitrateLadder: { width: number; height: number; fps: number; kbpsMax: number }[];
  inputPath: string;
  whipPath: string;
}

export interface LiveIocs {
  domains: string[];
  ips: string[];
  urls: string[];
  registryKeys: string[];
  droppedFiles: string[];
  mutexes: string[];
  verdictHint: 'clean' | 'suspicious' | 'malicious';
  signals: string[];
}

export function analysisWebSocketUrl(path: string): string {
  const base = `${location.origin}${ANALYSIS}`.replace(/^http/, 'ws');
  return `${base}${path}`;
}

/** Same-origin (prod) or dev-proxied HTTP URL for an interactive-svc path (e.g. the WHIP endpoint). */
export function interactiveHttpUrl(path: string): string {
  return `${location.origin}${INTERACTIVE}${path}`;
}

/** WebSocket URL for interactive-svc, carrying the access token as a query param (browsers
 * cannot set an Authorization header on a WS handshake). */
/**
 * In cookie mode the browser sends the session cookie on the WebSocket handshake, so the token no
 * longer goes in the URL. That matters beyond tidiness: URLs are recorded in ALB and CloudFront
 * access logs, upstream proxies and browser history, so the old form leaked a live access token into
 * places nobody thinks of as credential stores.
 *
 * A handshake cannot carry a custom header, which is why CSRF exempts upgrades; SameSite=Lax plus
 * origin checking is the defence there.
 */
export function interactiveWebSocketUrl(path: string): string {
  const base = `${location.origin}${INTERACTIVE}`.replace(/^http/, 'ws');
  if (COOKIE_AUTH) return `${base}${path}`;
  const sep = path.includes('?') ? '&' : '?';
  const auth = accessToken ? `${sep}access_token=${encodeURIComponent(accessToken)}` : '';
  return `${base}${path}${auth}`;
}

export interface SourceControl {
  source: PublicSampleSource;
  enabled: boolean;
  paused: boolean;
  maxItemsPerPoll: number;
  lastPollAt?: string;
  lastSuccessAt?: string;
  lastError?: string;
}

export interface QuarantineItem {
  id: string;
  source: string;
  externalId: string;
  reason: string;
  retryCount: number;
  status: string;
  createdAt: string;
}

export interface AdminGuestTier {
  tier: string;
  label: string;
  busy: number | null;
  capacity: number | null;
  source: string;
  reason?: string;
}

export interface AdminAnalysisStatus {
  queued: number;
  running: number;
  coordinator: { running: number; maxWorkers: number; stopped: boolean };
  fleet: {
    usedGuests?: number;
    maxGlobalGuests?: number;
    spendTodayUsd?: number;
    utilization?: number;
  };
  /**
   * MEASURED guest occupancy from the detonation hosts. Nullable throughout on purpose: a tier that
   * cannot be measured must read as unknown, never as zero, since zero looks like spare capacity.
   */
  guests?: {
    busy: number | null;
    capacity: number | null;
    complete: boolean;
    tiers: AdminGuestTier[];
    measuredAt: string | null;
  };
  /** Analyses in the dynamic stage - a DIFFERENT quantity from guests-busy, with its own ceiling. */
  inFlight?: { count: number; max: number | null };
  classLimits?: Array<{ cls: string; max: number | null; perWorker?: number | null; workers?: number; shared?: boolean }>;
}

export type GuestOs = 'linux' | 'windows' | 'android';
export type GuestArch = 'x86_64' | 'aarch64';

export interface StartSessionOptions {
  os?: GuestOs;
  arch?: GuestArch;
  network?: 'full' | 'simulated' | 'none';
  fileBase64?: string;
  fileName?: string;
}

export interface SessionInfo {
  sessionId: string;
  os: GuestOs;
  arch?: GuestArch;
  gatewayNode: string;
  streamUrl: string;
  vncUrl: string;
  expiresAt: string;
  networkMode: string;
  status?: string;
}

export interface GraphNode {
  id: string;
  type: string;
  label: string;
  props: Record<string, unknown>;
}
export interface GraphEdge {
  id: string;
  from: string;
  to: string;
  type: string;
}
/**
 * Investigation-workbench billing envelope: pivots/expands + native enrichments consume a free
 * daily allowance first, then spend credits. Returned by the graph endpoints so the UI can render
 * an honest meter (never a fabricated one).
 */
export interface GraphBillingInfo {
  allowance: { limit: number; used: number; remaining: number; resetAt: string };
  creditsCharged: number;
  creditsRemaining: number | null;
}
/**
 * What each federated store contributed to an expand.
 *
 * A number is a real neighbour count. `timeout`, `error` and `off` are the honest answers when a
 * store did not respond in time, threw, or is not wired on this deployment - all three distinct from
 * `0`, which is a genuine measurement meaning "asked, and there are none". A slow store must show as
 * fewer neighbours THAT SAY SO, never as a silently smaller graph.
 */
export type GraphSources = Record<string, number | 'timeout' | 'error' | 'off'>;
export interface GraphPivot {
  center: GraphNode;
  nodes: GraphNode[];
  edges: GraphEdge[];
  billing?: GraphBillingInfo;
  sources?: GraphSources;
  /** Non-fatal notes, e.g. how many guest-baseline neighbours were collapsed. */
  notes?: string[];
}
export interface GraphPivotBatch {
  centers: string[];
  nodes: GraphNode[];
  edges: GraphEdge[];
  unresolved: string[];
  billing?: GraphBillingInfo;
  sources?: GraphSources;
  notes?: string[];
}
export type ExtractedIocType =
  | 'url'
  | 'domain'
  | 'ipv4'
  | 'ipv6'
  | 'sha256'
  | 'sha1'
  | 'md5'
  | 'email'
  | 'cve'
  | 'mutex'
  | 'registry'
  | 'filepath'
  | 'bitcoin';
export interface ExtractedIoc {
  type: ExtractedIocType;
  value: string;
}
export interface IocExtractionResult {
  fileName: string;
  fileType: string;
  sizeBytes: number;
  textChars: number;
  iocs: ExtractedIoc[];
  counts: Partial<Record<ExtractedIocType, number>>;
  truncated: boolean;
}

export interface InvestigationNode {
  id: string;
  type: string;
  label: string;
  props?: Record<string, unknown>;
  x?: number;
  y?: number;
  pinned?: boolean;
  manual?: boolean;
  annotation?: string;
}
export interface InvestigationEdge {
  id: string;
  from: string;
  to: string;
  type: string;
  label?: string;
  manual?: boolean;
}
export interface InvestigationGraph {
  nodes: InvestigationNode[];
  edges: InvestigationEdge[];
}
export interface InvestigationNote {
  id: string;
  author: string;
  text: string;
  createdAt: string;
}
export interface InvestigationSummary {
  id: string;
  name: string;
  visibility: 'public' | 'private';
  nodes: number;
  edges: number;
  seeds: number;
  tags: string[];
  updatedAt: string;
}
export interface InvestigationDetail {
  id: string;
  name: string;
  description: string;
  visibility: 'public' | 'private';
  seeds: string[];
  tags: string[];
  graph: InvestigationGraph;
  notes: InvestigationNote[];
  shareToken: string;
  createdAt: string;
  updatedAt: string;
}
export interface EnrichmentKeyView {
  provider: string;
  last4: string;
  createdAt: string;
  updatedAt: string;
}
export interface EnrichmentProviderMeta {
  id: string;
  name: string;
  needsKey: boolean;
  supports: string[];
  keyUrl?: string;
}
export interface EnrichNodeInput {
  id?: string;
  type: string;
  value: string;
}
export interface EnrichmentAttribute {
  label: string;
  value: string;
}
export interface EnrichmentResultRow {
  node: string;
  provider: string;
  cached?: boolean;
  ok: boolean;
  found: boolean;
  verdict?: 'malicious' | 'suspicious' | 'clean' | 'unknown';
  score?: number;
  summary?: string;
  attributes: EnrichmentAttribute[];
  error?: string;
}
export interface GraphEnrichResponse {
  nodes: Array<{ id: string; type: string; label: string; props?: Record<string, unknown> }>;
  edges: Array<{ from: string; to: string; type: string; label?: string }>;
  results: EnrichmentResultRow[];
  billing?: GraphBillingInfo;
}

function runConfigBody(cfg: RunConfig) {
  return {
    visibility: cfg.visibility,
    network: cfg.network,
    interactive: cfg.interactive,
    captchaToken: cfg.captchaToken,
    ...(cfg.os && cfg.os !== 'auto' ? { os: cfg.os } : {}),
    ...(cfg.engine && cfg.engine !== 'auto' ? { engine: cfg.engine } : {}),
  };
}

export interface FeedItem {
  sha256: string;
  fileName: string | null;
  verdict: Verdict['verdict'];
  score: number;
  family?: string;
  analyzedAt: string;
  provenance?: SourceProvenance;
}

export interface Trends {
  window: string;
  topFamilies: { family: string; count: number }[];
  emergingC2: { indicator: string; type: string; prevalence: number }[];
  /** Genuinely most-submitted public samples; `submissions` is a COUNT, not a verdict score. */
  mostSubmitted: { sha256: string; fileName: string | null; submissions: number }[];
  generatedAt: string;
}

export interface KarmaProfile {
  userId: string;
  handle: string;
  submissions: number;
  firstToFind: number;
  usefulReports: number;
  karma: number;
}

// ---- Researcher Rank ----
export type LeaderboardWindow = 'all' | 'weekly' | 'monthly';

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  handle: string;
  points: number;
  /** Alias of `points` for back-compat. */
  karma: number;
  tier: string;
  submissions: number;
  firstFinds: number;
  streakCurrent: number;
  badges: string[];
}

export interface ResearcherActivity {
  kind: string;
  points: number;
  at: string;
  ref?: string | null;
}

export interface ResearcherProfile {
  handle: string;
  points: number;
  tier: string;
  rank: number | null;
  total: number;
  streakCurrent: number;
  streakLongest: number;
  submissions: number;
  firstFinds: number;
  usefulVotes: number;
  comments: number;
  tags: number;
  badges: string[];
  joinedAt: string;
  recent: ResearcherActivity[];
}

export interface MyRank extends Omit<ResearcherProfile, 'joinedAt'> {
  nextTier: string | null;
  pointsToNext: number | null;
}

/**
 * Base for public artifact URLs (badge/OG/embed) that must work OUTSIDE the SPA dev proxy,
 * e.g. a badge pasted into a blog. Defaults to same-origin `/api/analysis`; override with
 * VITE_PUBLIC_API_BASE in prod (e.g. https://api.threatlens.example).
 */
export const publicApiBase: string =
  (import.meta.env.VITE_PUBLIC_API_BASE as string | undefined) ??
  (import.meta.env.PROD ? location.origin : `${location.origin}/api/analysis`);

export interface ProcNodeDto {
  pid: number;
  ppid: number;
  image: string;
  cmdline: string;
  integrity: string;
  user: string;
  children: ProcNodeDto[];
  /** Memory-forensics/sandbox flag: the sample affected this process (injection/hollowing/monitored). */
  malwareAffected?: boolean;
  affectReason?: string;
  source?: 'cape' | 'memory';
  /** Per-process image hash when the monitor captured one (Sysmon md5 / dropped-file sha256). */
  md5?: string;
  sha256?: string;
}

export interface ExtractedConfig {
  family: string;
  c2: string[];
  keys: Record<string, string>;
  confidence: number;
}

export interface EvasionFinding {
  technique: string;
  name: string;
  evidence: string[];
}

export interface SuricataAlert {
  sid: number;
  msg: string;
  protocol: string;
  evidence: string;
}

export interface ReplayFrame {
  index: number;
  seq: number;
  tsMs: number;
  type: string;
  pid: number;
  summary: string;
  detail: Record<string, string | number>;
}

export interface TaintFlow {
  id: string;
  source: { kind: string; ref: string; seq: number };
  sink: { kind: string; ref: string; seq: number };
  label: string;
  confidence: number;
}

export interface DeepAnalysis {
  mode: string;
  frameCount: number;
  frames: ReplayFrame[];
  taintFlows: TaintFlow[];
  coverage: {
    totalEvents: number;
    basicBlocksApprox: number;
    modules: { module: string; events: number }[];
    coveragePct: number;
  };
  hasReplayLog: boolean;
}

export interface CrossOsRun {
  os: string;
  enginesUsed: string[];
  processCount: number;
  eventCount: number;
  screenshots: number;
  durationMs: number;
}

/** Why the platform has not analyzed every captured dropped payload yet. Mirrors the backend union. */
export type DroppedFanoutReason =
  | 'depth_limit'
  | 'per_parent_cap'
  | 'backlog_reserved_lane'
  | 'bytes_unavailable'
  | 'enqueue_failed';

export interface DroppedFanoutStatus {
  candidates: number;
  enqueued: number;
  pending: number;
  budget: number;
  reason?: DroppedFanoutReason;
  updatedAt: string;
}

/**
 * User-facing wording for a fan-out reason. Kept in sync with the backend describe helper.
 *
 * The `never` assignment keeps the switch exhaustive at COMPILE time, so adding a variant to the
 * union fails the build until it is handled here. It deliberately does not throw at RUN time: this
 * value crosses a service boundary, and the backend can deploy a sixth reason before the SPA does.
 * A string we have no wording for must degrade to a vaguer sentence, never take down the report page.
 */
export function describeDroppedFanoutReason(reason: DroppedFanoutReason): string {
  switch (reason) {
    case 'depth_limit':
      return 'This sample is already a dropped payload at the recursion limit, so its own drops are not analyzed further.';
    case 'per_parent_cap':
      return 'This sample dropped more payloads than one report analyzes as child samples.';
    case 'backlog_reserved_lane':
      return 'The sandbox fleet is busy, so these payloads are queued and analyzed as capacity frees up.';
    case 'bytes_unavailable':
      return 'The captured bytes for these payloads could not be read back for analysis.';
    case 'enqueue_failed':
      return 'Queueing these payloads for analysis failed. They will be retried.';
    default: {
      const unhandled: never = reason;
      void unhandled;
      return 'These payloads have not been analyzed as child samples yet.';
    }
  }
}

/** One target the sample touched, with how many times, plus any value it wrote. */
export interface ActivityEntry {
  name: string;
  count: number;
  value?: string;
}

/** A capped list of targets that always discloses the full total it was capped from. */
export interface ActivityGroup {
  entries: ActivityEntry[];
  totalDistinct: number;
  totalEvents: number;
}

export interface DynamicSummary {
  engine?: string;
  enginesUsed?: string[];
  /** Per-OS breakdown when a document was detonated on both Windows and Linux (cross-platform doc). */
  crossOs?: CrossOsRun[];
  os: string;
  durationMs: number;
  /**
   * The sample's CPU architecture was emulated in userland (qemu-user) rather than run natively.
   *
   * A successful run, not a degradation, but the reader needs it to bound what the results can mean:
   * kernel-level behaviour is unobservable under emulation, and the emulation is detectable by the
   * sample, so a short run may be evasion rather than an inert sample. Absent means native.
   */
  cpuEmulated?: boolean;
  processTree: ProcNodeDto;
  /** Full guest OS process tree with affected/monitored pids flagged; the sample-centric processTree
   *  stays the default view. Built from in-guest Sysmon on any run that has it, and replaced by the
   *  Volatility pslist once a memory pass has run. */
  fullProcessTree?: ProcNodeDto;
  /** How many processes the whole-guest view holds that the sample-scoped tree does not, so a
   *  correctly narrowed tree does not read as a shallow capture. Absent means unmeasured, which is
   *  not the same as zero. */
  hiddenProcessCount?: number;
  attack: { technique: string; name: string; evidence: string[] }[];
  /** Malware Behavior Catalog behaviours (VT `vt.behaviour.mbc`). */
  mbc?: { id: string; objective: string; behavior: string; evidence: string[] }[];
  /**
   * What the sample did to the filesystem, registry and OS objects, derived from the captured event
   * stream and scoped to the sample's own processes. Absent on very old reports.
   */
  activity?: {
    files: { written: ActivityGroup; deleted: ActivityGroup; read: ActivityGroup };
    registry: { written: ActivityGroup; read: ActivityGroup };
    mutexes: ActivityGroup;
    services: ActivityGroup;
    sampleScoped: boolean;
  };
  network: {
    dnsQueries: string[];
    httpRequests: string[];
    contactedIps: string[];
    /**
     * Which destinations the sandbox guest contacts on its own versus which the sample caused.
     * Computed at read time on the API, so reports finalized before this existed carry it too.
     * The arrays above are unchanged and still hold everything — this is a presentation split.
     */
    guestBaseline?: {
      sampleDns: string[];
      baselineDns: string[];
      sampleIps: string[];
      baselineIps: string[];
    };
    outboundBytes?: number;
    connectionCount?: number;
    distinctDestPorts?: number;
    httpTransactions?: HttpTransaction[];
    dnsResolutions?: DnsResolution[];
    tlsHandshakes?: TlsHandshake[];
    zeekConns?: {
      proto: string;
      service?: string;
      destIp: string;
      destPort: number;
      connState?: string;
      origBytes?: number;
      respBytes?: number;
      durationMs?: number;
    }[];
    /** Per-destination classification + ranking (config-derived / reputation-flagged / ids-alerted /
     *  unclassified-external / benign-infra / routine-local). Built at finalize. */
    destinations?: NetworkDestination[];
  };
  droppedFiles: {
    path: string;
    sha256: string;
    /** Size of the captured dropped file, when its bytes were stored. */
    sizeBytes?: number;
    /** True when the dropped file's bytes were captured + stored (a gated download is available). */
    stored?: boolean;
    verdict?: 'malicious' | 'suspicious' | 'clean' | 'unknown';
    score?: number;
    family?: string;
  }[];
  screenshots: number;
  memoryDumps?: number;
  eventCount: number;
  extractedConfig?: ExtractedConfig;
  evasion?: EvasionFinding[];
  suricataAlerts?: SuricataAlert[];
  sigmaDetections?: SigmaDetection[];
  memoryForensics?: {
    engine: string;
    os?: string;
    ok: boolean;
    findings: { plugin: string; kind: string; process?: string; pid?: number; detail: string; weight: number }[];
    networkEndpoints?: string[];
    commandLines?: string[];
  };
  /**
   * Lifecycle of the async Volatility memory pass: pending (in progress), complete, none (no image
   * captured), or failed (a pass was attempted but the image could not be analysed / the dump was
   * lost). `failed` is shown honestly and never as "no artifacts found".
   */
  memoryForensicsStatus?: 'pending' | 'complete' | 'none' | 'failed';
  /** When memoryForensicsStatus is 'failed', a short reason for display. */
  memoryForensicsFailReason?: string;
  /** Lifecycle of the async per-destination IP attribution pass (geo / ASN / registered org),
   *  which can land a minute or two after the report finalizes. */
  ipIntelStatus?: 'pending' | 'complete' | 'none';
  sandboxReports?: SandboxReport[];
  tlsIntercepted?: boolean;
  deepAnalysis?: DeepAnalysis;
  artifacts?: {
    pcap: boolean;
    screenshotCount: number;
    memoryDumpCount: number;
    /** Count of runtime-dropped files whose bytes were captured + stored (downloadable). */
    droppedCount?: number;
    unpackedCount?: number;
    configCount?: number;
    replayLog?: boolean;
    /** Short-lived presigned URLs for the sandbox screenshots (inline gallery). */
    screenshotUrls?: string[];
  };
}

export type NetworkDestinationClass =
  | 'config-derived'
  | 'reputation-flagged'
  | 'ids-alerted'
  | 'unclassified-external'
  | 'benign-infra'
  | 'routine-local';

/** Geo / autonomous-system attribution for an external destination. Every field except `source`
 *  and `fetchedAt` is best-effort and frequently absent: render nothing rather than a placeholder. */
export interface IpIntel {
  countryCode?: string;
  countryName?: string;
  city?: string;
  /** Autonomous system number in "AS15169" form. */
  asn?: string;
  /** Registered organization / AS owner, e.g. "Google LLC". */
  asOrg?: string;
  isp?: string;
  /** Ports an external scanner observed LISTENING on this host. NOT ports this sample contacted. */
  openPorts?: number[];
  tags?: string[];
  /** AbuseIPDB community reputation (0-100), display-only context; absent when the leg did not run. */
  abuseConfidenceScore?: number;
  abuseTotalReports?: number;
  abuseUsageType?: string;
  abuseLastReportedAt?: string;
  isTor?: boolean;
  source: 'shodan' | 'rdap' | 'cymru' | 'mixed' | 'abuseipdb';
  fetchedAt: string;
}

export interface NetworkDestination {
  destination: string;
  kind: 'ip' | 'domain';
  class: NetworkDestinationClass;
  ports?: number[];
  note?: string;
  ipIntel?: IpIntel;
}

export interface EngineDetection {
  engine: string;
  source: string;
  category: 'av' | 'yara' | 'hash' | 'static' | 'vendor-av';
  flagged: boolean;
  malwareName?: string;
}

export interface SigmaDetection {
  id: string;
  title: string;
  level: 'informational' | 'low' | 'medium' | 'high' | 'critical';
  attack: string[];
  matchedSelections: string[];
  evidence: string[];
}

export interface SandboxReport {
  engine: string;
  name: string;
  os: string;
  durationMs: number;
  processCount: number;
  attackTechniques: string[];
  sigmaDetections?: SigmaDetection[];
  network: { dnsQueries: string[]; httpRequests: string[]; contactedIps: string[] };
  droppedFiles: number;
  screenshots: number;
  extractedConfigFamily?: string | null;
  verdictScore?: number;
}

export interface HttpTransaction {
  method: string;
  host: string;
  path: string;
  status?: number;
  requestBytes?: number;
  responseBytes?: number;
  contentType?: string;
  userAgent?: string;
}

export interface DnsResolution {
  query: string;
  type?: string;
  answers: string[];
  rcode?: string;
  sinkholed?: boolean;
}

export interface TlsHandshake {
  sni: string;
  ja3?: string;
  ja3s?: string;
  ja4?: string;
  ja4s?: string;
  destIp?: string;
  destPort?: number;
}

export interface SimilarSample {
  id: string;
  fileName: string | null;
  fileType: string;
  verdict: string;
  score: number;
  family?: string;
  visibility?: string;
  similarity: number;
  matchedOn: string[];
}

export interface CollectionSummary {
  id: string;
  name: string;
  visibility: string;
  samples: number;
  iocs: number;
  updatedAt: string;
}

export interface CollectionDetail {
  id: string;
  name: string;
  description: string;
  visibility: 'public' | 'private';
  sampleHashes: string[];
  iocs: { type: string; value: string }[];
  notes: { id: string; author: string; text: string; createdAt: string }[];
  shareToken: string;
  createdAt: string;
  updatedAt: string;
}

export interface PeInfoDto {
  machine: string;
  timestamp: number;
  isDll: boolean;
  subsystem: number;
  imageBase?: number;
  entryPoint?: number;
  sections: { name: string; virtualSize: number; rawSize: number; entropy: number; md5?: string }[];
  imports: { dll: string; functions: string[] }[];
  importCount: number;
  exports?: { name: string; ordinal: number }[];
  exportCount?: number;
  resources?: { count: number; types: string[]; hasIcon: boolean; hasManifest: boolean };
  versionInfo?: Record<string, string>;
  richHeader?: { present: boolean; xorKey?: string; hash?: string };
  authenticode?: { present: boolean; signer?: string; issuer?: string; commonNames?: string[] };
  tlsCallbackCount?: number;
  isDotNet?: boolean;
  overlay?: { size: number; offset: number; md5: string; entropy: number } | null;
  hasOverlay: boolean;
}

export interface ElfInfoDto {
  class: string;
  endianness: string;
  type: string;
  machine: string;
  entryPoint: number;
  sections: string[];
  sectionDetails?: {
    name: string;
    type: string;
    address: number;
    offset: number;
    size: number;
    flags: string;
  }[];
  isStatic: boolean;
  isStripped: boolean;
  segments?: string[];
  neededLibraries?: string[];
  interpreter?: string;
  buildId?: string;
  dynamicSymbolCount?: number;
  runpath?: string[];
}

export interface MachoInfoDto {
  format: string;
  archCount: number;
  cpuType: string;
  bits: number;
  loadCommands: string[];
  segments?: string[];
  dylibs?: string[];
  uuid?: string;
  codeSigned?: boolean;
  minOsVersion?: string;
}

export interface ApkInfoDto {
  packageName?: string;
  permissions: string[];
  dangerousPermissions: string[];
  permhash: string;
  dexCount: number;
  nativeLibs: string[];
  signed: boolean;
  signerCommonNames: string[];
  minSdk?: number;
  targetSdk?: number;
  versionName?: string;
  activities?: string[];
  services?: string[];
  receivers?: string[];
  providers?: string[];
}

export interface EmbeddedFileDto {
  type: string;
  offset: number;
  size: number;
  sha256: string;
}

export interface EmailAttachmentDto {
  fileName: string;
  contentType?: string;
  size: number;
  sha256: string;
}

export interface EmailInfoDto {
  format: 'msg' | 'eml';
  from?: string;
  displayName?: string;
  senderEmail?: string;
  replyTo?: string;
  to?: string[];
  subject?: string;
  date?: string;
  messageId?: string;
  auth?: { spf?: string; dkim?: string; dmarc?: string };
  headerCount: number;
  urls: string[];
  attachments: EmailAttachmentDto[];
  suspicious: string[];
}

export interface QrHitDto {
  data: string;
  isUrl: boolean;
  source?: string;
}

export interface PublicReport {
  contentHash: string;
  fileName: string | null;
  fileType: string;
  sizeBytes: number;
  hashes: Record<string, string>;
  visibility?: 'public' | 'private';
  tlp?: 'clear' | 'green' | 'amber' | 'red';
  verdict: Verdict;
  packer: { packed: boolean; detections: string[]; overallEntropy: number; compiler?: string; linker?: string; tooling?: string[] };
  entropyMap?: { windowSize: number; values: number[] };
  embeddedFiles?: EmbeddedFileDto[];
  characteristics?: { magic?: string; mimeType?: string; metadata?: Record<string, string> };
  yara: { rule: string; tags: string[]; meta: Record<string, string> }[];
  capa: { capability: string; attackTechniques: string[]; evidence: string[] }[];
  iocs: { urls: string[]; domains: string[]; ipv4: string[]; registryKeys: string[]; emails?: string[]; filePaths?: string[]; bitcoin?: string[] };
  /** Curated printable strings extracted from the sample (new analyses only). Searchable via `strings:`. */
  strings?: string[];
  engines?: EngineDetection[];
  engineStats?: { total: number; flagged: number };
  /** Vendor-AV convergence state: expected vendor engines still missing show as an honest
   *  "pending"/"unavailable" note instead of a silent omission. */
  vendorAv?: {
    status: 'pending' | 'complete' | 'partial';
    missing?: string[];
    updatedAt?: string;
    attempts?: number;
    /** Retrying has STOPPED for the missing engines. Distinguishes "still coming" from "gave up", which
     *  the panel must not conflate: it used to promise background retries in both cases. */
    retriesExhausted?: boolean;
  };
  /**
   * A configured STATIC engine tier contributed NOTHING to this report (server sets it, e.g.
   * `av_tier_unavailable`). Every engine adapter fails open, so a dead tier is otherwise invisible:
   * the report just carries fewer lines and reads as a confident low detection count. Without this,
   * a malicious sample scanned while the AV tier is down renders as a clean "0 of N flagged".
   */
  staticDegraded?: boolean;
  staticDegradedReason?: string;
  pe?: PeInfoDto;
  elf?: ElfInfoDto;
  macho?: MachoInfoDto;
  apk?: ApkInfoDto;
  email?: EmailInfoDto;
  qr?: QrHitDto[];
  script?: {
    language: string;
    layers: string[];
    suspiciousFeatures: string[];
    deobfuscated: string[];
  };
  featureVector?: { schemaVersion: string; names: string[]; values: number[] };
  dynamic?: DynamicSummary;
  dynamicDegraded?: boolean;
  dynamicDegradedReason?: string;
  /**
   * Present when a fresh analysis for this hash is queued or executing RIGHT NOW. Only attached to a
   * report whose dynamic section is missing or degraded, so the page can tell the reader the platform
   * is already re-running it rather than presenting a hole with no explanation.
   */
  redetonation?: { queued: boolean };
  /**
   * Accounting for the dropped-file fan-out. `pending` payloads were captured but have not been
   * analyzed as child samples yet, and `reason` says why the platform has not gotten to them. Absent on
   * reports finalized before this ledger existed, which is why the UI treats it as optional rather
   * than inferring a reason it cannot know. Sits beside `dynamic` rather than inside it because the
   * ledger is recorded on the report record and is topped up after the detonation has finalized.
   */
  droppedFanout?: DroppedFanoutStatus;
  archiveExtraction?: {
    executablesFound: number;
    childrenDetonated: number;
    truncated: boolean;
    extractionFailed?: boolean;
    reason?: string;
    children?: Array<{ sha256: string; fileName: string }>;
  };
  provenance?: SourceProvenance;
  analyzedAt: string;
}

export type ReportRecord = { static: PublicReport; visibility: string } & Record<string, unknown>;

export interface ReportComment {
  id: string;
  authorName: string;
  body: string;
  at: string;
  authorId?: string | null;
}
export interface VoteTally {
  malicious: number;
  harmless: number;
  myVote?: 'malicious' | 'harmless' | null;
}
export interface CommunityOverlay {
  total: number;
  malicious: number;
  harmless: number;
  consensus: 'malicious' | 'harmless' | 'split' | null;
  agreement: number;
  confidence: number;
  hasQuorum: boolean;
  engineVerdict: 'clean' | 'suspicious' | 'malicious' | 'unknown' | 'insufficient';
  /** True when the community strongly contradicts the engine verdict (reliability caveat). */
  disputed: boolean;
}
export interface Attribution {
  attributed: boolean;
  family?: string;
  actor?: string;
  confidence: number;
  threshold: number;
  candidates: { family: string; confidence: number }[];
  evidence: { source: string; detail: string; weight: number }[];
}
export interface ReportDiff {
  a: string;
  b: string;
  verdict: { a: string; b: string; scoreA: number; scoreB: number };
  imphashMatch: boolean;
  similarity: number;
  iocDomains: { added: string[]; removed: string[]; common: number };
  capabilities: { added: string[]; removed: string[]; common: number };
  attack: { added: string[]; removed: string[]; common: number };
}

export interface ReputationResult {
  known: boolean;
  indicator?: string;
  verdict: Verdict['verdict'];
  score?: number;
  prevalence?: number;
  firstSeen?: string;
  lastSeen?: string;
}
